/** tm/pages/GovFeedInbox.tsx — 공고 검토함 · 대량 검토 (2026-09-30 22:07 확정안)
 *
 *  1년치 이상(수천 건)을 빠르게 등록하는 화면. GovPage 와 같은 토큰 · 같은 1열 필터.
 *   · 상단 바: [엑셀 올리기] [지금 수집] · 진행률(작업 있을 때만 5초 폴링) · 소스별 마지막 수집
 *   · 탭: 바로 등록 가능 · 확인 필요 · 애매 · 비추천 · 분석 대기 · 처리 완료
 *   · 표: 체크 → [선택 일괄 등록] / [선택 제외] · 칸(사업명·지역·분류·금액) 클릭 = 팝오버 수정 → draft 즉시 저장
 *   · 행 클릭 = 오른쪽 패널(AI 요약·근거 + 기존 GovProgramForm) · 편집은 draft 로 자동 저장 · ↑↓ ⏎ X Esc
 *  검토 패널이 열리면 3단: [좁은 목록] | [원문 뷰어(폭 조절·접기)] | [AI 요약 + 등록 양식] — 근거 클릭 = 원문 형광, 원문 글 선택 = 칸 채우기.
 *  「AI 학습」 서랍: 칸별 정확도 · 배운 규칙 · 다시 학습 · 새 규칙으로 다시 분석.
 */
import { Fragment, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import type { TMStore } from '../hooks';
import { C, T, R, Btn, SearchInput, Empty, PBar, REGION_CITIES } from '../components/ui';
import {
  emptyGovProgramForm, govProgramFormFromRow, govProgramFormToPayload, canSaveGovForm, isPastGovForm, REGIONS, type GovProgramFormState,
} from '../components/GovProgramForm';
import { GovTopBar, BUCKETS, PAST_NOTE, progPatchOf, parseBizinfoExcel, parseManwon, SOURCE_LABEL, fmtUsd, usageLines } from '../components/GovFeedParts';
import { GovFeedTable, FeedCellEditor, StarIcon, type FeedEditField } from '../components/GovFeedTable';
import GovFeedPanel, { FocusIcon, type DraftSaveState, type PanelBusy } from '../components/GovFeedPanel';
import GovFeedBulkDialog, { type BulkOutcome } from '../components/GovFeedBulkDialog';
import GovFeedDocViewer, { parseDates, type DocFocus, type PickField } from '../components/GovFeedDocViewer';
import GovFeedLearnDrawer from '../components/GovFeedLearnDrawer';
import { GOV_TAXONOMY, GOV_ARTS, GOV_TYPES } from '../components/GovTaxonomy';
import { BudgetMeter, BudgetBanner, BudgetDialog } from '../components/GovFeedBudget';
import * as api from '../api';
import type { GovProgram, GovFeedStats, GovFeedRow, GovFeedDetail, GovFeedBucket, GovFeedSourceStat, GovFeedRulesInfo, GovFeedFacets, GovFeedSort, GovFeedSortKey, GovFeedScopeMode } from '../types';

interface Props {
  store: TMStore;
  /** 상단 바 왼쪽(「등록된 공고 | 공고 검토함」 전환) */
  topLeft?: React.ReactNode;
  /** 검토 대기 건수가 바뀌면 알려 준다 (전환 스위치 숫자) */
  onCounts?: (newCount: number) => void;
  /** 열자마자 이 검토함 공고를 [원문 | 조건] 집중 화면으로 연다 (fphub 리포트 → 로움 · 2026-10-08) */
  openGroupId?: string | null;
}

// GovPage 와 같은 1열 스타일
const secTitle: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: C.sub, marginBottom: 8, paddingBottom: 6, borderBottom: `1px solid ${C.border}` };
const filterItem = (active: boolean): React.CSSProperties => ({
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  padding: '6px 10px', borderRadius: R.sm, cursor: 'pointer', fontSize: 12,
  fontWeight: active ? 600 : 400, color: active ? C.accent : C.text,
  background: active ? C.accentLt : 'transparent', transition: 'all 0.12s',
});
const countStyle = (active: boolean): React.CSSProperties => ({ fontSize: 11, color: active ? C.accent : C.muted, fontWeight: 600, fontVariantNumeric: 'tabular-nums' });
const smallBtn: React.CSSProperties = { fontSize: T.xs, padding: '6px 10px', whiteSpace: 'nowrap' };
const linkBtn: React.CSSProperties = { padding: '3px 8px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: C.white, cursor: 'pointer', fontSize: T.xs, color: C.sub, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 3 };
const textBtn: React.CSSProperties = { border: 'none', background: 'transparent', padding: '2px 4px', cursor: 'pointer', fontSize: T.xs, fontWeight: 600, color: C.accent, whiteSpace: 'nowrap' };

type Tab = GovFeedBucket | 'done';
/** 지역 칸 순서 — 수도권 먼저 (분석 순서 · 정렬과 같다, 2026-10-03) */
const REGION_GRID = ['서울', '경기', '인천', '부산', '대구', '광주', '대전', '울산', '세종', '강원', '충북', '충남', '전북', '전남', '경북', '경남', '제주'];
/** 지역·분류·정렬 기억 — 다음에 열어도 그대로 (지도사 2026-10-03 「지역은 골라서 그것만 먼저 검수」) */
const FILTER_KEY = 'gf.filter.v1';
interface SavedFilter { regions: string[]; nat: boolean; cities: string[]; cats: string[]; sort: GovFeedSort | null; star: boolean }
const SORT_KEYS: GovFeedSortKey[] = ['fit', 'name', 'region', 'cat', 'amount', 'deadline', 'collected'];
function loadFilter(): SavedFilter {
  const empty: SavedFilter = { regions: [], nat: false, cities: [], cats: [], sort: null, star: false };
  try {
    const v: unknown = JSON.parse(lsGet(FILTER_KEY) || 'null');
    if (!v || typeof v !== 'object') return empty;
    const o = v as Record<string, unknown>;
    const strs = (x: unknown) => (Array.isArray(x) ? x.filter((y): y is string => typeof y === 'string').slice(0, 40) : []);
    const so = o.sort as Record<string, unknown> | null | undefined;
    const sort: GovFeedSort | null = so && typeof so === 'object' && SORT_KEYS.includes(so.k as GovFeedSortKey) && (so.dir === 'asc' || so.dir === 'desc')
      ? { k: so.k as GovFeedSortKey, dir: so.dir } : null;
    return { regions: strs(o.regions), nat: o.nat === true, cities: strs(o.cities), cats: strs(o.cats), sort, star: o.star === true };
  } catch { return empty; }
}
const countTag: React.CSSProperties = { fontSize: 10.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums' };
/** 출처 필터 기본 순서 — 서버 stats.sources 가 있으면 그 순서(새 출처가 늘어도 화면을 고치지 않는다, 2026-10-07) */
const SOURCES = ['bizinfo', 'sbiz24', 'naver']; // 2026-10-08 금융위 · 신보 · 기보는 뉴스로 옮김
const PAGE_SIZE = 100;
const CHUNK = 2000;
const BULK_CHUNK = 200;
const SELECT_ALL_MAX = 5000;

const n = (v: number | null | undefined) => (v ?? 0).toLocaleString();
function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (x: number) => String(x).padStart(2, '0');
  return `${p(d.getMonth() + 1)}.${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}
/** 화면 배치 기억 (원문 폭·접기·목록 폭) — 없거나 막혀도 기본값 */
const lsGet = (k: string): string | null => { try { return localStorage.getItem(k); } catch { return null; } };
const lsSet = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* 기억 못 해도 된다 */ } };
const LIST_W = 272;
const SLIM_W = 60;
const FORM_MIN = 420;
const VIEWER_MIN = 300;
/** 집중 모드 — 위 띠 높이 · 원문/양식 나눔 비율(%) 한계 · 세로로 쌓는 폭 */
const STRIP_H = 40;
const SPLIT_MIN = 30;
const SPLIT_MAX = 75;
const FOCUS_NARROW = 1100;
const clampSplit = (v: number) => Math.max(SPLIT_MIN, Math.min(SPLIT_MAX, Math.round(v * 10) / 10));
const splitOf = (k: string, fb: number) => { const v = Number(lsGet(k)); return Number.isFinite(v) && v > 0 ? clampSplit(v) : fb; };
const stripBtn: React.CSSProperties = { height: 26, padding: '0 10px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: C.white, cursor: 'pointer', fontSize: T.xs, color: C.sub, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 5, boxSizing: 'border-box', flexShrink: 0 };
const stripIcon: React.CSSProperties = { ...stripBtn, width: 26, padding: 0, justifyContent: 'center', fontSize: T.sm };
const stripKbd: React.CSSProperties = { display: 'inline-block', minWidth: 16, padding: '0 4px', borderRadius: 4, border: `1px solid ${C.borderDk}`, fontSize: 10, fontWeight: 700, lineHeight: '16px', textAlign: 'center', color: C.sub };
const FOCUS_CSS = `
.gf-fsplit{position:relative;background:${C.bgAlt};touch-action:none;outline:none;transition:background .12s}
.gf-fsplit::after{content:"";position:absolute;top:50%;left:50%;border-radius:2px;background:${C.borderDk};transition:background .12s}
.gf-fsplit.h{cursor:col-resize;border-left:1px solid ${C.border};border-right:1px solid ${C.border}}
.gf-fsplit.h::after{width:2px;height:32px;margin:-16px 0 0 -1px}
.gf-fsplit.v{cursor:row-resize;border-top:1px solid ${C.border};border-bottom:1px solid ${C.border}}
.gf-fsplit.v::after{width:32px;height:2px;margin:-1px 0 0 -16px}
.gf-fsplit:hover,.gf-fsplit:focus-visible,.gf-fsplit.on{background:${C.accentLt}}
.gf-fsplit:hover::after,.gf-fsplit:focus-visible::after,.gf-fsplit.on::after{background:${C.accent}}
.gf-sbtn:hover:not(:disabled){border-color:${C.borderDk};color:${C.text};background:${C.bgAlt}}
@keyframes gfFocusIn{from{opacity:0}to{opacity:1}}
`;
const errMsg = (e: unknown, fb: string) => (e instanceof Error && e.message ? e.message : fb);
const isTyping = (el: EventTarget | null) => {
  const t = el as HTMLElement | null;
  if (!t || !t.tagName) return false;
  if (t.tagName === 'INPUT' && /^(checkbox|radio)$/.test((t as HTMLInputElement).type)) return false;
  return /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) || t.isContentEditable;
};
/** 폼 = prefill 위에 draft (서버 규칙과 같다) */
const formOf = (d: GovFeedDetail) => govProgramFormFromRow({ ...d.prefill, ...(d.draft || {}) } as GovProgram);

export default function GovFeedInbox({ store, topLeft, onCounts, openGroupId }: Props) {
  // ── 필터 · 탭 ──
  const [tab, setTab] = useState<Tab>('ready');
  const [doneReview, setDoneReview] = useState<'approved' | 'excluded'>('approved');
  const saved0 = useMemo(loadFilter, []);
  const [regions, setRegions] = useState<string[]>(saved0.regions);
  const [nat, setNat] = useState(saved0.nat);
  const [cities, setCities] = useState<string[]>(saved0.cities);
  const [cats, setCats] = useState<string[]>(saved0.cats);
  const [sort, setSort] = useState<GovFeedSort | null>(saved0.sort);
  /** 관심(별표)만 보기 (2026-10-03) — 탭·지역·분류와 함께 걸린다 */
  const [starOnly, setStarOnly] = useState(saved0.star);
  const [catOpen, setCatOpen] = useState<string | null>(null);
  const [facets, setFacets] = useState<GovFeedFacets | null>(null);
  useEffect(() => { lsSet(FILTER_KEY, JSON.stringify({ regions, nat, cities, cats, sort, star: starOnly })); }, [regions, nat, cities, cats, sort, starOnly]);
  /** 광역을 하나만 고른 게 아니면 시·군 선택은 뜻이 없다 */
  const namedRegions = regions.filter(r => r !== '전국');
  useEffect(() => { if (namedRegions.length !== 1 && cities.length) setCities([]); }, [namedRegions.length, cities.length]);
  const [source, setSource] = useState('');
  const [recur, setRecur] = useState(false);
  const [dupOnly, setDupOnly] = useState(false);
  const [aiErr, setAiErr] = useState(false);
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => { const t = setTimeout(() => setQ(search.trim()), 300); return () => clearTimeout(t); }, [search]);

  // ── 목록 ──
  const [rows, setRows] = useState<GovFeedRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const loadSeq = useRef(0);
  const rowsRef = useRef<GovFeedRow[]>([]);
  rowsRef.current = rows;

  // 선택(체크) — 다른 페이지 행도 담을 수 있게 행 자체를 보관
  const [sel, setSel] = useState<Map<string, GovFeedRow>>(new Map());
  const anchorRef = useRef<string | null>(null);
  const [selectingAll, setSelectingAll] = useState(false);
  const [cursorId, setCursorId] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [savingIds, setSavingIds] = useState<Set<string>>(new Set());
  const [edit, setEdit] = useState<{ row: GovFeedRow; field: FeedEditField; anchor: DOMRect } | null>(null);
  const [bulkRows, setBulkRows] = useState<GovFeedRow[] | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  // ── 패널 ──
  const [selId, setSelId] = useState<string | null>(null);
  const selIdRef = useRef<string | null>(null);
  selIdRef.current = selId;
  const [detailKey, setDetailKey] = useState(0);
  const [detail, setDetail] = useState<GovFeedDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [form, setForm] = useState<GovProgramFormState>(emptyGovProgramForm());
  const [applyMode, setApplyMode] = useState<'update' | 'new'>('update');
  const [busy, setBusy] = useState<PanelBusy>(null);
  const [saveState, setSaveState] = useState<DraftSaveState>('idle');

  // draft 자동 저장 — 마지막으로 저장한 값(json)과 보내기 전 대기(pending)
  const savedJson = useRef<{ id: string; json: string } | null>(null);
  const pending = useRef<{ id: string; payload: Partial<GovProgram>; json: string; timer: ReturnType<typeof setTimeout> } | null>(null);

  // ── 현황 · 가져오기 ──
  const [stats, setStats] = useState<GovFeedStats | null>(null);
  const [imp, setImp] = useState<{ phase: 'read' | 'send'; sent: number; total: number } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [toast, setToast] = useState('');
  /** 토스트의 「되돌리기」 — 그 토스트 글과 짝일 때만 보인다 */
  const [undoAct, setUndoAct] = useState<{ msg: string; run: () => void } | null>(null);
  useEffect(() => { if (!toast) return; const t = setTimeout(() => { setToast(''); setUndoAct(null); }, undoAct && undoAct.msg === toast ? 6000 : 3500); return () => clearTimeout(t); }, [toast, undoAct]);

  // ── 폭 (열 숨김 · 필터 접기) ──
  const rootRef = useRef<HTMLDivElement>(null);
  const tableBoxRef = useRef<HTMLDivElement>(null);
  const [rootW, setRootW] = useState(1200);
  const [tableW, setTableW] = useState(900);
  useEffect(() => {
    const ro = new ResizeObserver(() => {
      if (rootRef.current) setRootW(rootRef.current.clientWidth);
      if (tableBoxRef.current) setTableW(tableBoxRef.current.clientWidth);
    });
    if (rootRef.current) ro.observe(rootRef.current);
    if (tableBoxRef.current) ro.observe(tableBoxRef.current);
    return () => ro.disconnect();
  }, []);
  const panelOpen = !!selId;
  const hideFilters = panelOpen; // 3단 검토 — 필터 칸은 접고 원문·양식에 자리를 준다

  // ── 3단 검토: [목록] | [원문] | [양식] ──
  const [viewerOpen, setViewerOpen] = useState(() => lsGet('gf.viewerOpen') !== '0');
  const [viewerW, setViewerW] = useState(() => Number(lsGet('gf.viewerW')) || 500);
  const [listPref, setListPref] = useState<'slim' | 'wide' | null>(() => { const v = lsGet('gf.list'); return v === 'slim' || v === 'wide' ? v : null; });
  const [dragging, setDragging] = useState(false);
  const [focus, setFocus] = useState<DocFocus | null>(null);
  useEffect(() => { lsSet('gf.viewerOpen', viewerOpen ? '1' : '0'); }, [viewerOpen]);
  useEffect(() => { const t = setTimeout(() => lsSet('gf.viewerW', String(Math.round(viewerW))), 300); return () => clearTimeout(t); }, [viewerW]);
  useEffect(() => { if (listPref) lsSet('gf.list', listPref); }, [listPref]);
  // 원문·양식 최소 폭이 안 나오면 목록은 띠로 (사용자 선택보다 우선)
  const listCanWiden = rootW >= LIST_W + (viewerOpen ? VIEWER_MIN + 6 : 34) + FORM_MIN;
  const listSlim = !listCanWiden || (listPref ? listPref === 'slim' : rootW < 1240);
  const listW = listSlim ? SLIM_W : LIST_W;
  const viewerMax = Math.max(VIEWER_MIN, rootW - listW - FORM_MIN - 6);
  const viewerWEff = Math.max(VIEWER_MIN, Math.min(viewerW, viewerMax));
  const startDrag = (e: React.MouseEvent) => {
    e.preventDefault();
    const x0 = e.clientX; const w0 = viewerWEff;
    setDragging(true);
    document.body.style.cursor = 'col-resize';
    const mv = (ev: MouseEvent) => setViewerW(Math.max(VIEWER_MIN, Math.min(viewerMax, w0 + ev.clientX - x0)));
    const up = () => { setDragging(false); document.body.style.cursor = ''; window.removeEventListener('mousemove', mv); window.removeEventListener('mouseup', up); };
    window.addEventListener('mousemove', mv);
    window.addEventListener('mouseup', up);
  };

  // ── 집중 모드: 사이드바·상단·탭·목록을 덮고 [원문 | 양식] 만 화면 가득 ──
  //   같은 원문·양식 인스턴스를 그대로 고정 덮개로 옮겨 그린다(선택·편집·스크롤이 그대로 이어진다)
  const [focusOn, setFocusOn] = useState(() => lsGet('gf.focus') === '1');
  const [splitH, setSplitH] = useState(() => splitOf('gf.focusSplit', 55));
  const [splitV, setSplitV] = useState(() => splitOf('gf.focusSplitV', 50));
  const [viewerOnly, setViewerOnly] = useState(false);
  const [fDrag, setFDrag] = useState(false);
  const [winW, setWinW] = useState(() => (typeof window === 'undefined' ? 1440 : window.innerWidth));
  const focusBoxRef = useRef<HTMLDivElement>(null);
  useEffect(() => { lsSet('gf.focus', focusOn ? '1' : '0'); }, [focusOn]);
  useEffect(() => { const t = setTimeout(() => lsSet('gf.focusSplit', String(splitH)), 300); return () => clearTimeout(t); }, [splitH]);
  useEffect(() => { const t = setTimeout(() => lsSet('gf.focusSplitV', String(splitV)), 300); return () => clearTimeout(t); }, [splitV]);
  useEffect(() => {
    const h = () => setWinW(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  const focusActive = focusOn && panelOpen;
  const focusNarrow = winW < FOCUS_NARROW;
  const viewerOnlyEff = focusActive && focusNarrow && viewerOnly;
  const startFocusDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    const box = focusBoxRef.current;
    if (!box) return;
    e.preventDefault();
    const el = e.currentTarget;
    const vertical = focusNarrow;
    try { el.setPointerCapture(e.pointerId); } catch { /* 캡처 못 해도 끌기는 된다 */ }
    setFDrag(true);
    const mv = (ev: PointerEvent) => {
      const r = box.getBoundingClientRect();
      if (vertical) setSplitV(clampSplit(((ev.clientY - r.top - STRIP_H) / Math.max(1, r.height - STRIP_H)) * 100));
      else setSplitH(clampSplit(((ev.clientX - r.left) / Math.max(1, r.width)) * 100));
    };
    const up = () => {
      setFDrag(false);
      el.removeEventListener('pointermove', mv);
      el.removeEventListener('pointerup', up);
      el.removeEventListener('pointercancel', up);
    };
    el.addEventListener('pointermove', mv);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
  };
  /** 나눔 막대 키보드 — ←→(세로로 쌓였으면 ↑↓) 2%씩 · Home 기본 */
  const onSplitKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const dec = focusNarrow ? 'ArrowUp' : 'ArrowLeft';
    const inc = focusNarrow ? 'ArrowDown' : 'ArrowRight';
    const set = focusNarrow ? setSplitV : setSplitH;
    if (e.key === dec || e.key === inc) { e.preventDefault(); set(v => clampSplit(v + (e.key === inc ? 2 : -2))); }
    else if (e.key === 'Home') { e.preventDefault(); set(focusNarrow ? 50 : 55); }
  };

  // ── AI 학습 ──
  const [learnOpen, setLearnOpen] = useState(false);
  const [budgetOpen, setBudgetOpen] = useState(false);
  const [learnInfo, setLearnInfo] = useState<GovFeedRulesInfo | null>(null);
  useEffect(() => { api.getGovFeedRules().then(r => { if (r.data) setLearnInfo(r.data); }).catch(() => {}); }, []);
  const learnPct = learnInfo?.accuracy.overall != null ? Math.round(learnInfo.accuracy.overall * 100) : null;

  // ═══ 현황(stats) — 작업이 있을 때만 5초 폴링 ═══
  const onCountsRef = useRef(onCounts);
  onCountsRef.current = onCounts;
  const statsBusy = useRef(false);
  const [scopeBusy, setScopeBusy] = useState(false);
  const refreshStats = useCallback(async () => {
    if (statsBusy.current) return; // 앞 요청이 끝나기 전엔 또 보내지 않는다 (2026-10-03 — 겹친 현황 요청이 서버를 막던 문제)
    statsBusy.current = true;
    try {
      const r = await api.getGovFeedStats();
      if (r.data) {
        setStats(r.data);
        onCountsRef.current?.(r.data.counts.new);
      }
    } catch { /* 현황은 없어도 화면은 쓸 수 있다 */ } finally { statsBusy.current = false; }
  }, []);
  useEffect(() => { void refreshStats(); }, [refreshStats]);
  /** 분석 범위 바꾸기 — 지방까지 넓히면 서버가 바로 이어서 분석 */
  // 비용이 드는 범위(체크한 것만 · 수도권 · 지방)로 바꿀 때는 그 단추를 한 번 더 눌러야 한다 — 브라우저 확인 창 없이(2026-10-27
  //  크롬 판독 중 「체크한 것만」을 잘못 눌러 ★ 47건이 바로 AI 심층 분석에 들어감 · 「체크한 것만」은 확인이 아예 없었다)
  const [scopeArm, setScopeArm] = useState<GovFeedScopeMode | null>(null);
  useEffect(() => { if (!scopeArm) return undefined; const t = window.setTimeout(() => setScopeArm(null), 6000); return () => window.clearTimeout(t); }, [scopeArm]);
  const changeScope = useCallback(async (mode: GovFeedScopeMode) => {
    setScopeArm(null);
    setScopeBusy(true);
    try {
      const r = await api.setGovFeedScope(mode);
      if (r.data) setStats(prev => (prev ? { ...prev, scope: r.data } : prev));
      void refreshStats();
    } catch (e) { setToast(errMsg(e, '분석 범위를 바꾸지 못했습니다')); } finally { setScopeBusy(false); }
  }, [refreshStats]);
  const jobKind = stats?.job?.kind ?? null;
  const syncing = jobKind === 'sync' || !!stats?.sources?.some(x => x.running);
  const sourceLabel = (k: string) => stats?.sources?.find(x => x.key === k)?.label || SOURCE_LABEL[k] || k;
  const batchesInFlight = stats?.batches?.inFlight ?? 0;
  // 폴링: 작업·수집 중이면 5초, Batch 결과만 기다리면 30초, 없으면 멈춤
  const pollMs = jobKind || syncing ? 5000 : batchesInFlight > 0 ? 30000 : 0;
  useEffect(() => {
    if (!pollMs) return;
    const t = setInterval(() => { void refreshStats(); }, pollMs);
    return () => clearInterval(t);
  }, [pollMs, refreshStats]);

  // ═══ 목록 ═══
  const query = useMemo((): api.GovFeedGroupsQuery => {
    const base: api.GovFeedGroupsQuery = {
      q, recur, dup: dupOnly, source: source || undefined, ai: aiErr ? 'error' : '',
      regions, nat: nat && namedRegions.length > 0, cities: namedRegions.length === 1 ? cities : [], cats,
      // 분석 대기 = 고르기 목록 — ★(분석) 하거나 ✕(버리기) 한 공고는 빠지고 «아직 안 고른 것»만 남는다 (2026-10-04 지도사)
      sort: sort?.k, dir: sort?.dir, star: starOnly ? true : tab === 'pending' ? 'none' : false,
    };
    if (tab === 'done') return { ...base, review: doneReview };
    return { ...base, review: 'new', bucket: tab };
  }, [tab, doneReview, q, recur, dupOnly, source, aiErr, regions, nat, cities, cats, sort, namedRegions.length, starOnly]);

  const load = useCallback(async (pg: number, append: boolean) => {
    const seq = ++loadSeq.current;
    setListLoading(true);
    try {
      const r = await api.getGovFeedGroups({ ...query, page: pg, size: PAGE_SIZE, counts: pg === 1 });
      if (seq !== loadSeq.current || !r.data) return;
      const got = r.data.rows || [];
      if (pg === 1) setFacets(r.data.facets || null);
      setRows(prev => (append ? [...prev, ...got.filter(g => !prev.some(p => p.id === g.id))] : got));
      setTotal(Number(r.data.total) || 0);
      setPage(pg);
      setHasMore(pg * PAGE_SIZE < (Number(r.data.total) || 0) && got.length > 0);
    } catch (e) {
      if (seq === loadSeq.current) setToast(errMsg(e, '목록을 불러오지 못했습니다'));
    } finally {
      if (seq === loadSeq.current) setListLoading(false);
    }
  }, [query]);
  // 필터를 연달아 누르면 마지막 것만 (0.15초 모아서) 불러온다
  useEffect(() => {
    setSel(new Map()); setCursorId(null); setRowErrors({});
    const t = setTimeout(() => { void load(1, false); }, 150);
    return () => clearTimeout(t);
  }, [load]);

  // 작업 하나가 끝나면(선별 → 분석 → 끝) 목록을 새로 읽는다
  const prevJob = useRef<string | null>(null);
  useEffect(() => {
    if (prevJob.current && prevJob.current !== jobKind) void load(1, false);
    prevJob.current = jobKind;
  }, [jobKind, load]);

  // 무한 스크롤 — 표 끝의 표지가 보이면 다음 페이지
  const sentinelRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore) return;
    const io = new IntersectionObserver(es => { if (es[0].isIntersecting && !listLoading) void load(page + 1, true); }, { root: scrollRef.current, rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, listLoading, load, page, rows.length]);

  const patchRow = useCallback((id: string, fn: (r: GovFeedRow) => GovFeedRow) => {
    setRows(prev => prev.map(r => (r.id === id ? fn(r) : r)));
    setSel(prev => { const r = prev.get(id); if (!r) return prev; const m = new Map(prev); m.set(id, fn(r)); return m; });
  }, []);

  // ═══ 관심 별표 (2026-10-03) ═══
  const selRef = useRef(sel);
  selRef.current = sel;
  const toggleStar = useCallback(async (ids: string[], on: boolean) => {
    if (!ids.length) return;
    const was = (id: string) => !!(rowsRef.current.find(r => r.id === id)?.starred ?? selRef.current.get(id)?.starred);
    const changed = ids.filter(id => was(id) !== on);
    const apply = (v: boolean) => {
      ids.forEach(id => patchRow(id, r => ({ ...r, starred: v })));
      setDetail(d => (d && ids.includes(d.group.id) ? { ...d, group: { ...d.group, starred: v } } : d));
    };
    apply(on);
    if (changed.length) setFacets(f => (f ? { ...f, starred: Math.max(0, (f.starred || 0) + (on ? changed.length : -changed.length)) } : f));
    try {
      const res = await api.starGovFeed(ids, on);
      // 교통정리 (2026-10-03): 분석 전 공고의 ★ = 심층 분석 요청(자동) · 분석된 공고의 ★ = 관심 표시
      const fresh = ids.filter(id => { const s = (rowsRef.current.find(r => r.id === id) ?? selRef.current.get(id))?.aiStatus; return s === 'none' || s === 'past' || s === 'queued'; }).length;
      const dq = res.data?.dequeued ?? 0;
      if (on && fresh) setToast(`${n(fresh)}건 심층 분석 요청 — 차례로 분석해 결과 탭으로 옮깁니다${ids.length > fresh ? ` · 이미 분석된 ${n(ids.length - fresh)}건은 관심 표시만` : ''}`);
      else if (!on && dq) setToast(`${n(dq)}건 분석 요청을 취소했어요 (비용 안 듦)`);
      else if (ids.length > 1) setToast(on ? `${n(ids.length)}건을 관심에 담았어요` : `${n(ids.length)}건을 관심에서 뺐어요`);
      if (!on && dq) ids.forEach(id => patchRow(id, r => (r.aiStatus === 'queued' && r.fit == null ? { ...r, aiStatus: 'none' } : r)));
    } catch (e) {
      apply(!on);
      if (changed.length) setFacets(f => (f ? { ...f, starred: Math.max(0, (f.starred || 0) - (on ? changed.length : -changed.length)) } : f));
      setToast(errMsg(e, '관심 표시를 저장하지 못했습니다'));
    }
  }, [patchRow]);

  // 관심만 다시 분석 — 고친 값을 먼저 학습시키고 새 규칙으로 (2026-10-03 지도사)
  const [reBusy, setReBusy] = useState(false);
  const reanalyzeStarred = useCallback(async () => {
    if (reBusy) return;
    setReBusy(true);
    try {
      const p = await api.reanalyzeStarredGovFeed(true);
      const d = p.data;
      if (!d || !d.matched) { setToast('다시 분석할 «분석된» 관심 공고가 없습니다 — 분석 대기의 ★ 는 자동으로 심층 분석됩니다'); return; }
      const won = d.maxWon != null ? `최대 약 ${n(Math.ceil(d.maxWon / 100) * 100)}원` : '조금';
      const msg = [`이미 분석된 관심 공고 ${n(d.matched)}건을 AI 학습을 반영해 다시 분석할까요?`, '',
        '· 「분석 대기」의 ★ 는 여기에 들어가지 않습니다 — 그쪽은 ★ 를 누른 순간 자동으로 심층 분석됩니다',
        '· 먼저 고치신 값(리포트 사업명 등)을 AI에 학습시킨 뒤 분석합니다', '· 고치신 칸은 그대로 남고, 고치지 않은 칸만 새 결과로 바뀝니다',
        `· 비용 ${won} (일괄 처리 — 보통 1시간 안에 결과)`,
        ...(d.skipped ? [`· 마감된 일회성 공고 ${n(d.skipped)}건은 빼고 합니다`] : []),
        '· 「다시 분석」은 이 단추를 누를 때만 — ★ 를 새로 눌러도 분석된 공고는 다시 분석하지 않습니다',
        ...(d.remaining < d.matched ? [`· 오늘 분석 한도로 ${n(d.remaining)}건만 먼저 — 나머지는 내일`] : [])].join('\n');
      if (!window.confirm(msg)) return;
      const r = await api.reanalyzeStarredGovFeed(false);
      setToast(`관심 ${n(r.data?.queued ?? 0)}건 — 고친 값 학습 후 분석 대기열에 넣습니다 (결과는 「확인 필요」 등 탭으로 돌아옵니다)`);
      setTimeout(() => { void refreshStats(); void load(1, false); }, 4000);
    } catch (e) { setToast(errMsg(e, '다시 분석을 시작하지 못했습니다')); } finally { setReBusy(false); }
  }, [reBusy, refreshStats, load]);

  // ═══ draft 저장 ═══
  /** 부분 payload 를 서버 draft 에 병합 저장 → 행 요약 갱신 */
  const saveDraft = useCallback(async (id: string, patch: Partial<GovProgram>): Promise<'saved'> => {
    await api.saveGovFeedDraft(id, patch);
    patchRow(id, r => ({ ...r, edited: true, program: { ...(r.program || {}), ...progPatchOf(patch) } }));
    return 'saved';
  }, [patchRow]);

  const flushPending = useCallback(() => {
    const p = pending.current;
    if (!p) return;
    clearTimeout(p.timer);
    pending.current = null;
    saveDraft(p.id, p.payload)
      .then(st => { if (savedJson.current?.id === p.id) savedJson.current = { id: p.id, json: p.json }; if (selIdRef.current === p.id) setSaveState(st); })
      .catch(() => { if (selIdRef.current === p.id) setSaveState('error'); });
  }, [saveDraft]);

  // 패널 편집 → 0.7초 뒤 draft 자동 저장
  useEffect(() => {
    const g = detail?.group;
    if (!g || g.id !== selId || g.review !== 'new') return;
    const payload = govProgramFormToPayload(form);
    const json = JSON.stringify(payload);
    if (!savedJson.current || savedJson.current.id !== g.id || savedJson.current.json === json) return;
    if (pending.current) clearTimeout(pending.current.timer);
    const id = g.id;
    pending.current = {
      id, payload, json,
      timer: setTimeout(() => {
        pending.current = null;
        setSaveState('saving');
        saveDraft(id, payload)
          .then(st => { if (selIdRef.current === id) { savedJson.current = { id, json }; setSaveState(st); } })
          .catch(() => { if (selIdRef.current === id) setSaveState('error'); });
      }, 700),
    };
  }, [form, detail, selId, saveDraft]);

  // ═══ 상세 ═══
  // 승인 완료 공고는 폼에 «등록된 공고» 값을 띄운다 — 옛 분석 초안을 고쳐 봐야 저장되지 않던 문제(2026-10-10 지도사 「리포트에서 눌러 들어가 바꿨는데 저장이 안 되네」)
  const regOf = (d: GovFeedDetail): GovProgram | null => {
    if (d.group.review !== 'approved') return null;
    const id = d.group.govProgramId || d.group.dupProgramId || (d.dupProgram && d.dupProgram.id) || '';
    return id ? store.govPrograms.find(p => p.id === id) || null : null;
  };
  const regShown = useRef('');   // 등록된 공고 값을 폼에 띄운 묶음 id — 등록 목록이 늦게 와도 한 번 다시 띄운다
  const applyDetail = useCallback((d: GovFeedDetail) => {
    const reg = regOf(d);
    regShown.current = reg ? d.group.id : '';
    const f = reg ? govProgramFormFromRow(reg) : formOf(d);
    setDetail(d);
    setForm(f);
    savedJson.current = { id: d.group.id, json: JSON.stringify(govProgramFormToPayload(f)) };
    setSaveState('idle');
    setApplyMode(d.dupProgram ? 'update' : 'new');
  }, [store.govPrograms]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (detail && detail.group.review === 'approved' && regShown.current !== detail.group.id && regOf(detail)) applyDetail(detail);
  }, [store.govPrograms, detail]);   // eslint-disable-line react-hooks/exhaustive-deps

  /** 승인 완료 공고 — 폼을 등록된 공고에 저장(PUT /gov-programs/:id) · fphub 는 로움 도장이 바뀌면 5분 안에 다시 받는다 */
  async function handleSaveRegistered() {
    const d = detail;
    const reg = d ? regOf(d) : null;
    if (!d || !reg || busy) return;
    if (!canSaveGovForm(form)) { setToast(form.report_name.trim() ? '사업명(FP 전송용)을 입력하세요' : '리포트 사업명을 입력하세요'); return; }
    blur();
    setBusy('saveReg');
    try {
      await api.updateGovProgram(reg.id, govProgramFormToPayload(form));
      setToast('등록된 공고에 저장 — 매칭은 바로 · fphub 리포트는 5분 안에 반영');
      void store.refresh();
    } catch (e) { setToast(errMsg(e, '저장 실패')); }
    finally { setBusy(null); }
  }

  useEffect(() => {
    if (!selId) { setDetail(null); return; }
    let cancelled = false;
    setDetailLoading(true);
    api.getGovFeedGroup(selId)
      .then(r => { if (!cancelled && r.data) applyDetail(r.data); })
      .catch(e => { if (!cancelled) { setDetail(null); setToast(errMsg(e, '공고를 불러오지 못했습니다')); } })
      .finally(() => { if (!cancelled) setDetailLoading(false); });
    return () => { cancelled = true; };
  }, [selId, detailKey, applyDetail]);

  const openRow = useCallback((id: string | null) => {
    flushPending();
    setFocus(null);
    setSelId(id);
    if (id) setCursorId(id);
  }, [flushPending]);

  // 바깥에서 지정한 공고 바로 열기 — 승인된 공고라 「검토 완료 · 승인」 목록을 깔고, 집중 모드로 원문 | 조건
  const openedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!openGroupId || openedRef.current === openGroupId) return;
    openedRef.current = openGroupId;
    setTab('done'); setDoneReview('approved'); setFocusOn(true);
    openRow(openGroupId);
  }, [openGroupId, openRow]);

  // 화면을 떠날 때(「등록된 공고」로 전환 등) 못 보낸 자동 저장을 마저 보낸다
  const flushRef = useRef(flushPending);
  flushRef.current = flushPending;
  useEffect(() => () => flushRef.current(), []);

  // 선택한 행이 보이게
  useEffect(() => {
    const id = selId || cursorId;
    if (!id) return;
    // 표 칸만 움직인다 — scrollIntoView 는 overflow:hidden 인 바깥 칸(앱 전체)까지 밀어 올려, 집중 모드를 끈 뒤 화면이 위로 밀리고 아래가 비었다 (2026-10-01)
    const box = scrollRef.current;
    const tr = box?.querySelector(`tr[data-row="${id}"]`) as HTMLElement | null;
    if (box && tr && box.offsetParent !== null) {
      const b = box.getBoundingClientRect();
      const r = tr.getBoundingClientRect();
      const head = (box.querySelector('thead') as HTMLElement | null)?.offsetHeight || 0; // 고정 머리줄 아래로
      if (r.top < b.top + head) box.scrollTop += r.top - b.top - head;
      else if (r.bottom > b.bottom) box.scrollTop += r.bottom - b.bottom;
    }
    // 혹시 밀려 올라간 바깥 칸(overflow:hidden)이 있으면 제자리로
    for (let el = focusBoxRef.current?.parentElement || null; el && el !== document.body; el = el.parentElement) {
      if (el.scrollTop && getComputedStyle(el).overflowY === 'hidden') el.scrollTop = 0;
    }
  }, [selId, cursorId, focusActive]);

  /** 처리 후 다음 건 — 처리한 행은 지금 탭에서 빠진다 */
  function afterDone(id: string) {
    const list = rowsRef.current;
    const idx = list.findIndex(r => r.id === id);
    const rest = list.filter(r => r.id !== id);
    setRows(rest);
    setTotal(t => Math.max(0, t - 1));
    setSel(prev => { if (!prev.has(id)) return prev; const m = new Map(prev); m.delete(id); return m; });
    const cand = rest[idx] || rest[idx - 1] || null;
    if (selIdRef.current === id) openRow(cand ? cand.id : null);
    if (cand) setCursorId(cand.id);
    if (!rest.length && hasMore) void load(1, false);
    void refreshStats();
  }

  /** 분석 대기 「고르기」 — ✕ 버리기: 목록에서 바로 빠지고 다음 줄로 · 토스트에서 되돌리기 (2026-10-04) */
  async function dropRow(id: string) {
    const row = rowsRef.current.find(r => r.id === id);
    if (!row) return;
    afterDone(id);
    try {
      await api.excludeGovFeed(id);
      const nm = row.title.length > 26 ? `${row.title.slice(0, 26)}…` : row.title;
      const msg = `버렸어요 — ${nm}`;
      setToast(msg);
      setUndoAct({ msg, run: () => { void (async () => {
        try { await api.restoreGovFeed(id); setToast('되돌렸어요 — 다시 고를 목록에 있습니다'); void load(1, false); void refreshStats(); }
        catch (e) { setToast(errMsg(e, '되돌리지 못했습니다')); }
      })(); } });
    } catch (e) { setToast(errMsg(e, '버리지 못했습니다')); void load(1, false); }
  }
  /** 분석 대기 「고르기」 — ★ 분석: 심층 분석 요청 후 목록에서 빠지고 다음 줄로 (「★ 관심」에서 볼 수 있음) */
  function pickRow(id: string) {
    void toggleStar([id], true);
    afterDone(id);
    setToast('★ 심층 분석 요청 — 「관심」에서 볼 수 있어요');
  }
  const triage = tab === 'pending' && !starOnly;

  // ═══ 패널 동작 ═══
  const blur = () => { (document.activeElement as HTMLElement | null)?.blur?.(); };

  async function handleApprove() {
    const d = detail;
    if (!d || busy || d.group.review !== 'new') return;
    if (!canSaveGovForm(form)) { setToast(form.report_name.trim() ? '사업명(FP 전송용)을 입력하세요' : '리포트 사업명을 입력하세요'); return; }
    blur();
    if (pending.current?.id === d.group.id) { clearTimeout(pending.current.timer); pending.current = null; }
    const mode: 'new' | 'update' = d.dupProgram && applyMode === 'update' ? 'update' : 'new';
    setBusy('approve');
    try {
      const r = await api.approveGovFeed(d.group.id, {
        program: govProgramFormToPayload(form), mode, programId: mode === 'update' ? d.dupProgram!.id : undefined,
      });
      setToast(`${mode === 'update' ? '갱신' : '등록'} 완료${r.data?.warning === 'duplicate' ? ' · 같은 이름·기관 공고가 이미 있어요' : ''}`);
      void store.refresh(); // 「등록된 공고」 목록(store.govPrograms)을 새로 — GovPage 등록 후와 같게
      afterDone(d.group.id);
    } catch (e) { setToast(errMsg(e, '등록 실패')); }
    finally { setBusy(null); }
  }

  async function handleExclude() {
    const d = detail;
    if (!d || busy || d.group.review !== 'new') return;
    blur();
    if (pending.current?.id === d.group.id) { clearTimeout(pending.current.timer); pending.current = null; }
    setBusy('exclude');
    try { await api.excludeGovFeed(d.group.id); setToast('제외함 — 「처리 완료」에서 되돌릴 수 있어요'); afterDone(d.group.id); }
    catch (e) { setToast(errMsg(e, '제외 실패')); }
    finally { setBusy(null); }
  }

  async function handleRestore() {
    const d = detail;
    if (!d || busy) return;
    blur();
    setBusy('restore');
    try { await api.restoreGovFeed(d.group.id); setToast('검토 대기로 되돌림'); afterDone(d.group.id); }
    catch (e) { setToast(errMsg(e, '복원 실패')); }
    finally { setBusy(null); }
  }

  /** mode: 'batch'(기본 · 할인) | 'now'(바로 · 정가) — 2026-10-03 지도사 「항상 배치 … 지금 바로도」 */
  async function handleAnalyze(mode: 'batch' | 'now' = 'batch') {
    const d = detail;
    if (!d || busy) return;
    blur();
    const id = d.group.id;
    setBusy('analyze');
    if (mode === 'now') patchRow(id, r => ({ ...r, aiStatus: 'running' }));
    try {
      const r = await api.analyzeGovFeed(id, mode);
      const here = selIdRef.current === id;
      const sync = (x: GovFeedDetail) => patchRow(id, row => ({
        ...row, aiStatus: x.group.aiStatus, fit: x.group.fit, screen: x.group.screen, bucket: x.group.bucket,
        unsureCount: x.ai ? (x.ai.unsure || []).length : row.unsureCount,
        program: x.group.program ?? { ...(row.program || {}), ...progPatchOf({ ...x.prefill, ...(x.draft || {}) }) },
      }));
      if (r.status === 'done') { sync(r.detail); if (here) applyDetail(r.detail); setToast('AI 분석 완료'); }
      else if (r.status === 'queued') {
        sync(r.detail); patchRow(id, row => ({ ...row, starred: true }));
        if (here) applyDetail(r.detail);
        setToast(r.already ? '이미 일괄 분석 중입니다 — 보통 1시간 안에 결과 (급하면 「지금 바로」)' : '일괄 분석에 넣었어요 (50% 할인) — 보통 1시간 안에 결과가 이 자리에 들어옵니다');
      }
      else if (r.status === 'timeout') {
        setToast('분석이 길어져 뒤에서 계속합니다 — 잠시 뒤 다시 불러와요');
        setTimeout(() => { if (selIdRef.current === id) setDetailKey(k => k + 1); }, 20000);
      } else if (r.status === 'failed') { if (r.detail) { sync(r.detail); if (here) applyDetail(r.detail); } setToast(r.error); }
      else if (r.status === 'budget') { setToast('AI 예산 한도로 분석하지 않았습니다 — 예산을 올려 주세요'); patchRow(id, x => ({ ...x, aiStatus: d.group.aiStatus })); void refreshStats(); }
      else { setToast(r.error); patchRow(id, x => ({ ...x, aiStatus: d.group.aiStatus })); }
    } finally { setBusy(null); void refreshStats(); }
  }

  const idx = selId ? rows.findIndex(r => r.id === selId) : -1;
  const focusTitle = (detail && detail.group.id === selId ? (detail.items[0]?.title || detail.group.title) : rows[idx]?.title) || '';
  // 패널로 끝 쪽까지 내려오면 다음 페이지를 미리 읽는다
  useEffect(() => {
    if (idx >= 0 && hasMore && !listLoading && idx >= rows.length - 5) void load(page + 1, true);
  }, [idx, hasMore, listLoading, rows.length, load, page]);
  const move = useCallback((delta: number) => {
    const list = rowsRef.current;
    if (!list.length) return;
    if (selIdRef.current) {
      const i = list.findIndex(r => r.id === selIdRef.current);
      const nx = list[Math.max(0, Math.min(list.length - 1, i + delta))];
      if (nx && nx.id !== selIdRef.current) openRow(nx.id);
      return;
    }
    setCursorId(cur => {
      const i = cur ? list.findIndex(r => r.id === cur) : -1;
      const nx = list[Math.max(0, Math.min(list.length - 1, i < 0 ? 0 : i + delta))];
      return nx ? nx.id : cur;
    });
  }, [openRow]);

  // ═══ 선택(체크) ═══
  const toggleCheck = useCallback((id: string, shift: boolean) => {
    const list = rowsRef.current;
    const anchor = anchorRef.current;
    anchorRef.current = id;
    setSel(prev => {
      const m = new Map(prev);
      const row = list.find(r => r.id === id);
      if (!row) return prev;
      const on = !m.has(id);
      if (shift && anchor && anchor !== id) {
        const a = list.findIndex(r => r.id === anchor);
        const b = list.findIndex(r => r.id === id);
        if (a >= 0 && b >= 0) {
          list.slice(Math.min(a, b), Math.max(a, b) + 1).forEach(r => { if (on) m.set(r.id, r); else m.delete(r.id); });
          return m;
        }
      }
      if (on) m.set(id, row); else m.delete(id);
      return m;
    });
  }, []);
  const allShownChecked = rows.length > 0 && rows.every(r => sel.has(r.id));
  const toggleAllShown = () => setSel(prev => {
    const m = new Map(prev);
    if (allShownChecked) rows.forEach(r => m.delete(r.id)); else rows.forEach(r => m.set(r.id, r));
    return m;
  });

  /** 탭 전체 선택 — 보이지 않는 페이지까지 읽어 담는다 (최대 5,000건) */
  async function selectAllInTab() {
    if (selectingAll) return;
    setSelectingAll(true);
    try {
      const m = new Map<string, GovFeedRow>();
      rows.forEach(r => m.set(r.id, r));
      let pg = 1;
      for (;;) {
        const r = await api.getGovFeedGroups({ ...query, page: pg, size: 200 });
        const got = r.data?.rows || [];
        got.forEach(x => m.set(x.id, x));
        if (got.length < 200 || m.size >= SELECT_ALL_MAX || pg * 200 >= (r.data?.total || 0)) break;
        pg += 1;
      }
      setSel(m);
      if (m.size >= SELECT_ALL_MAX) setToast(`한 번에 ${n(SELECT_ALL_MAX)}건까지 선택합니다`);
    } catch (e) { setToast(errMsg(e, '전체 선택 실패')); }
    finally { setSelectingAll(false); }
  }

  // ═══ 일괄 처리 ═══
  async function runBulkApprove(ids: string[], onProgress: (done: number, total: number) => void): Promise<BulkOutcome[]> {
    flushPending();
    setBulkBusy(true);
    const out: BulkOutcome[] = [];
    let dupMissing = 0;
    try {
      for (let i = 0; i < ids.length; i += BULK_CHUNK) {
        const chunk = ids.slice(i, i + BULK_CHUNK);
        try {
          const res = await api.bulkApproveGovFeed(chunk);
          const byId = new Map((res.data?.results || []).map(x => [x.id, x] as const));
          chunk.forEach(id => {
            const x = byId.get(id);
            if (x?.warning === 'dupMissing') dupMissing += 1;
            out.push(x ? { id, ok: x.ok, error: x.error } : { id, ok: false, error: '결과 없음' });
          });
        } catch (e) { chunk.forEach(id => out.push({ id, ok: false, error: errMsg(e, '등록 실패') })); }
        onProgress(out.length, ids.length);
      }
    } finally {
      setBulkBusy(false);
    }
    // 결과 반영 — 성공은 표에서 빼고, 실패는 남겨 사유 표시 + 선택 유지
    const okIds = new Set(out.filter(o => o.ok).map(o => o.id));
    const fails = out.filter(o => !o.ok);
    setRows(prev => prev.filter(r => !okIds.has(r.id)));
    setTotal(t => Math.max(0, t - okIds.size));
    setSel(prev => { const m = new Map(prev); okIds.forEach(id => m.delete(id)); return m; });
    setRowErrors(prev => { const e = { ...prev }; okIds.forEach(id => delete e[id]); fails.forEach(f => { e[f.id] = f.error || '등록 실패'; }); return e; });
    if (selIdRef.current && okIds.has(selIdRef.current)) openRow(null);
    setToast(`${n(okIds.size)}건 등록${fails.length ? ` · ${n(fails.length)}건 실패 — 표에 표시` : ' 완료'}${dupMissing ? ` · 지워진 기존 공고 ${n(dupMissing)}건은 새로 등록` : ''}`);
    if (okIds.size) void store.refresh();
    void refreshStats();
    return out;
  }

  async function handleBulk(action: 'exclude' | 'restore') {
    const ids = Array.from(sel.keys());
    if (!ids.length || bulkBusy) return;
    setBulkBusy(true);
    try {
      const r = await api.bulkGovFeed(ids, action);
      const done = r.data?.updated ?? 0;
      const skip = r.data?.skipped ?? 0;
      setToast(`${n(done)}건 ${action === 'exclude' ? '제외' : '복원'}${skip ? ` · ${n(skip)}건은 그대로(상태가 달라서)` : ''}`);
      if (selIdRef.current && sel.has(selIdRef.current)) openRow(null);
      setSel(new Map());
      await load(1, false);
      void refreshStats();
    } catch (e) { setToast(errMsg(e, '일괄 처리 실패')); }
    finally { setBulkBusy(false); }
  }

  // ═══ 칸 편집 저장 ═══
  async function saveCell(row: GovFeedRow, patch: Partial<GovProgram>): Promise<boolean> {
    setSavingIds(s => new Set(s).add(row.id));
    try {
      await saveDraft(row.id, patch);
      setRowErrors(e => { if (!e[row.id]) return e; const x = { ...e }; delete x[row.id]; return x; });
      if (selIdRef.current === row.id) {
        // 열린 패널 폼에도 같은 값 — 다시 저장하지 않게 기준값도 옮긴다
        setForm(f => {
          const nf = govProgramFormFromRow({ ...govProgramFormToPayload(f), ...patch } as GovProgram);
          savedJson.current = { id: row.id, json: JSON.stringify(govProgramFormToPayload(nf)) };
          return nf;
        });
      }
      return true;
    } catch (e) { setToast(errMsg(e, '저장 실패')); return false; }
    finally { setSavingIds(s => { const x = new Set(s); x.delete(row.id); return x; }); }
  }

  // ═══ 원문 → 양식 ═══
  const onQuote = useCallback((quote: string, label: string) => {
    setViewerOpen(true);
    setFocus({ quote, label, nonce: Date.now() });
  }, []);
  const PICK_WORD: Record<PickField, string> = { name: '사업명', report_name: '리포트 사업명', amount: '지원금액', agency: '수행기관', deadline: '마감일', apply_start: '신청 시작일', period: '신청기간' };
  const onPick = useCallback((f: PickField, raw: string) => {
    const t = raw.replace(/\s+/g, ' ').trim();
    const ds = parseDates(t);
    setForm(p => {
      if (f === 'name') return { ...p, name: t.slice(0, 80) };
      if (f === 'report_name') {
        // 공고문 속 공식 사업명 (2026-10-01) — 지난 공고는 FP 이름도 따라간다(같았거나 비었을 때)
        const rn = t.replace(/^[「『"]|[」』"]$/g, '').slice(0, 200);
        const linked = isPastGovForm(p) && (!p.name.trim() || p.name.trim() === p.report_name.trim());
        return { ...p, report_name: rn, name: linked ? rn : p.name };
      }
      if (f === 'agency') return { ...p, agency: t.slice(0, 80) };
      if (f === 'amount') { const m = parseManwon(t); return { ...p, amount: t.slice(0, 60), amount_max_manwon: m != null ? String(m) : p.amount_max_manwon }; }
      if (f === 'deadline' && ds.length) return { ...p, deadline: ds[ds.length - 1], first_come: false };
      if (f === 'apply_start' && ds.length) return { ...p, apply_start: ds[0] };
      if (f === 'period' && ds.length >= 2) return { ...p, apply_start: ds[0], deadline: ds[ds.length - 1], first_come: false };
      return p;
    });
    setToast(`${PICK_WORD[f]}에 넣었어요`);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ═══ 키보드 — ↑↓ 이동 · ⏎ 등록(패널)/열기 · X 제외 · Space 선택 · Esc 닫기 ═══
  const keyRef = useRef<(e: KeyboardEvent) => void>(() => {});
  keyRef.current = (e: KeyboardEvent) => {
    if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;
    if (edit || bulkRows || learnOpen || budgetOpen || isTyping(e.target)) return;
    const tag = (e.target as HTMLElement | null)?.tagName;
    if ((tag === 'BUTTON' || tag === 'A' || tag === 'INPUT') && (e.key === 'Enter' || e.key === ' ')) return;
    // 집중 모드 — F 켜기/끄기 (한글 자판이어도 e.code), Esc 는 패널을 닫지 않고 집중 모드만 끈다
    if (e.code === 'KeyF' && !e.shiftKey) {
      e.preventDefault();
      if (focusActive) { setFocusOn(false); return; }
      const id = selId || cursorId || rowsRef.current[0]?.id || null;
      if (!id) return;
      if (!selId) openRow(id);
      setFocusOn(true);
      return;
    }
    if (e.key === 'Escape' && focusActive) { e.preventDefault(); setFocusOn(false); return; }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); move(e.key === 'ArrowDown' ? 1 : -1); return; }
    if (e.key === 'Escape') { if (selId) openRow(null); else setCursorId(null); return; }
    if (e.key === 'Enter') {
      if (selId) { e.preventDefault(); void handleApprove(); }
      else if (cursorId) { e.preventDefault(); openRow(cursorId); }
      return;
    }
    if (e.code === 'KeyX' && selId) { e.preventDefault(); void handleExclude(); return; }
    if (e.code === 'KeyX' && triage && cursorId) { e.preventDefault(); void dropRow(cursorId); return; }   // 분석 대기: 커서 줄 버리기
    // S — 관심 별표: 패널의 공고 → 체크한 공고들 → 커서 행
    if (e.code === 'KeyS' && !e.shiftKey) {
      const ids = selId ? [selId] : sel.size ? Array.from(sel.keys()) : cursorId ? [cursorId] : [];
      if (!ids.length) return;
      e.preventDefault();
      const all = ids.every(id => !!(rowsRef.current.find(r => r.id === id)?.starred ?? sel.get(id)?.starred ?? (detail?.group.id === id && detail.group.starred)));
      if (triage && !all && ids.length === 1) { pickRow(ids[0]); return; }   // 분석 대기: ★ 하고 다음 줄로
      void toggleStar(ids, !all);
      if (triage && !all) ids.forEach(id => afterDone(id));
      return;
    }
    if (e.key === ' ' && !selId && cursorId) { e.preventDefault(); toggleCheck(cursorId, e.shiftKey); }
  };
  useEffect(() => {
    const h = (e: KeyboardEvent) => keyRef.current(e);
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  // ═══ 수집 · 엑셀 ═══
  async function handleSync() {
    try {
      const r = await api.syncGovFeed();
      const st = r.data?.sources || {};
      const word = { started: '시작', running: '이미 수집 중', nokey: '키 없음 — 건너뜀' } as const;
      const parts = Object.entries(st).map(([k, v]) => `${sourceLabel(k)} ${word[v] || v}`);
      setToast(parts.length ? `수집 — ${parts.join(' · ')}` : r.data?.alreadyRunning ? '이미 수집 중입니다' : '수집을 시작했어요');
    } catch (e) { setToast(errMsg(e, '수집 시작 실패')); }
    void refreshStats();
    setTimeout(() => { void refreshStats(); }, 1500);
  }

  async function handlePipeline(retryErrors?: boolean) {
    try {
      const r = await api.runGovFeedPipeline(retryErrors);
      setToast(r.data?.alreadyRunning ? '이미 AI 작업 중입니다' : retryErrors ? '오류 건을 다시 분석합니다' : 'AI 선별·분석을 시작했어요');
    } catch (e) { setToast(errMsg(e, 'AI 작업 시작 실패')); }
    void refreshStats();
    setTimeout(() => { void refreshStats(); }, 1500);
  }

  async function handleFile(f: File) {
    setImp({ phase: 'read', sent: 0, total: 0 });
    try {
      const { rows: all, old } = await parseBizinfoExcel(f);
      if (!all.length) { setToast(`2025년 이후 등록된 공고가 없습니다${old ? ` (이전 공고 ${n(old)}건 건너뜀)` : ''}`); return; }
      let inserted = 0; let updated = 0;
      setImp({ phase: 'send', sent: 0, total: all.length });
      for (let i = 0; i < all.length; i += CHUNK) {
        const chunk = all.slice(i, i + CHUNK);
        const last = i + CHUNK >= all.length;
        const r = await api.importGovFeed(chunk, last);
        inserted += r.data?.inserted ?? 0;
        updated += r.data?.updated ?? 0;
        setImp({ phase: 'send', sent: Math.min(all.length, i + chunk.length), total: all.length });
      }
      setToast(`엑셀 반영 — 새 공고 ${n(inserted)} · 갱신 ${n(updated)}${old ? ` · 2025년 이전 ${n(old)}건 제외` : ''}`);
      await load(1, false);
    } catch (e) {
      setToast(`엑셀 올리기 실패: ${errMsg(e, '알 수 없는 오류')}`);
    } finally {
      setImp(null);
      if (fileRef.current) fileRef.current.value = '';
      void refreshStats();
      setTimeout(() => { void refreshStats(); }, 1500);
    }
  }

  // ═══ 파생 ═══
  const counts = stats?.counts;
  // 지역·분류 등으로 좁혔으면 탭 숫자도 그 조건으로 (목록 응답의 facets — 2026-10-03)
  const narrowed = !!(regions.length || cats.length || source || recur || dupOnly || aiErr || q || starOnly);
  const tabCount = (k: Tab): number | undefined => {
    if (k === 'done') return counts ? counts.approved + counts.excluded : undefined;
    if (narrowed && facets?.buckets && tab !== 'done') return facets.buckets[k];
    return counts?.buckets?.[k];
  };
  const selCount = sel.size;
  const selRows = useMemo(() => Array.from(sel.values()), [sel]);
  const canApprove = tab !== 'done' && selCount > 0;
  const shownTotal = total;

  // ═══ 상단 바 — 진행 상태 · 소스 ═══
  const bt = stats?.batches;
  const batchLine = bt && bt.inFlight > 0 ? (
    <span title={`일괄 처리 중 — 보통 1시간 안에(최대 24시간) 결과 · 묶음 ${bt.inFlight}개${bt.lastEndedAt ? ` · 마지막 완료 ${fmtDateTime(bt.lastEndedAt)}` : ''}`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: T.xs, fontWeight: 600, color: C.purple, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', cursor: 'default' }}>
      <span style={{ width: 6, height: 6, borderRadius: 3, background: C.purple, animation: 'gfPulse 1.6s ease-in-out infinite' }} />
      일괄 분석 중 {n(bt.requestsInFlight)}건
      <style>{'@keyframes gfPulse{0%,100%{opacity:1}50%{opacity:.3}}'}</style>
    </span>
  ) : null;
  function statusLine(): React.ReactNode {
    const mutedS: React.CSSProperties = { fontSize: T.xs, color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
    const bar = (done: number, tot: number, text: string) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <span style={{ fontSize: T.xs, fontWeight: 600, color: C.accent, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', overflow: 'hidden', textOverflow: 'ellipsis' }}>{text}</span>
        {tot > 0 && <div style={{ width: 80, flexShrink: 0 }}><PBar value={(done / tot) * 100} h={4} /></div>}
      </div>
    );
    if (imp) return imp.phase === 'read' ? bar(0, 0, '엑셀 읽는 중…') : bar(imp.sent, imp.total, `엑셀 올리는 중 ${n(imp.sent)}/${n(imp.total)}`);
    const job = stats?.job;
    if (job) {
      const text = job.kind === 'sync' ? `${job.source ? `${sourceLabel(job.source)} ` : ''}수집 중${job.total ? ` ${n(job.done)}/${n(job.total)}` : ''}`
        : job.kind === 'screen' ? `선별 ${n(job.done)}/${n(job.total)}${counts && counts.aiQueued ? ` · 분석 대기 ${n(counts.aiQueued)}` : ''}`
          : `분석 ${n(job.done)}/${n(job.total)}${counts && counts.pending ? ` · 선별 대기 ${n(counts.pending)}` : ''}`;
      return <>{bar(job.done, job.total, text)}{batchLine && <span style={{ marginLeft: 8, flexShrink: 0 }}>{batchLine}</span>}</>;
    }
    if (!stats) return null;
    // 수기 판독 — 정부지원사업 AI 전체 꺼짐(선별 · 분석 · 짧은 이름 · 묶기 · 다시 읽기 · 학습) · 「AI 시작」 단추를 보이지 않는다 (2026-10-17 지도사 「카드에서 결제되잖아」)
    if (stats.scope?.mode === 'manual') return <span title="검토함 위 「심층 분석」 칸에서 다른 범위를 고르면 다시 켜집니다" style={{ ...mutedS, fontWeight: 600 }}>수기 판독 · AI 꺼짐(비용 없음)</span>;
    if (stats.lastError) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <span title={stats.lastError} style={{ ...mutedS, color: C.red, fontWeight: 600 }}>AI 작업 멈춤 — {stats.lastError}</span>
          {stats.aiConfigured && <button onClick={() => handlePipeline()} style={linkBtn}>다시 시작</button>}
        </div>
      );
    }
    if (!stats.aiConfigured) return <span style={mutedS}>AI 키가 없어 선별·분석을 못 합니다</span>;
    if (batchLine) return batchLine;
    if (counts && (counts.pending > 0 || counts.aiQueued > 0)) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <span style={mutedS}>선별 대기 {n(counts.pending)} · 분석 대기 {n(counts.aiQueued)}</span>
          <button onClick={() => handlePipeline()} style={linkBtn}>AI 시작</button>
        </div>
      );
    }
    return null;
  }

  const sources: GovFeedSourceStat[] = stats?.sources || [];
  const syncDisabled = syncing;
  const syncTip = syncing ? '수집 중입니다'
    : `기업마당 · 소상공인24 · 네이버에서 최근 공고를 가져옵니다${stats && !stats.keyConfigured ? ' (기업마당은 API 키가 있어야 합니다 — 엑셀로 올려도 됩니다)' : ''}`;

  const filterActive = !!(regions.length || cats.length || source || recur || dupOnly || aiErr || q);
  const clearFilters = () => { setRegions([]); setCities([]); setCats([]); setSource(''); setRecur(false); setDupOnly(false); setAiErr(false); setSearch(''); };
  const toggleIn = (list: string[], v: string) => (list.includes(v) ? list.filter(x => x !== v) : [...list, v]);
  const firstDir = (k: GovFeedSortKey): 'asc' | 'desc' => (k === 'fit' || k === 'amount' || k === 'collected' ? 'desc' : 'asc');
  const onSort = (k: GovFeedSortKey) => setSort(cur => (!cur || cur.k !== k ? { k, dir: firstDir(k) } : cur.dir === firstDir(k) ? { k, dir: cur.dir === 'asc' ? 'desc' : 'asc' } : null));
  /** 지금 걸린 지역·분류 — 표 위 한 줄 요약(필터 칸을 접은 3단·집중 모드에서도 보이게) */
  const scopeText = [
    regions.length ? `${regions.join('·')}${namedRegions.length === 1 && cities.length ? ` ${cities.join('·')}` : ''}${nat && namedRegions.length && !regions.includes('전국') ? ' +전국' : ''}` : '',
    cats.length ? cats.join('·') : '',
  ].filter(Boolean).join(' · ');
  const fc = facets;
  const cnt = (v: number | undefined, on: boolean) => (fc ? <span style={{ ...countTag, color: on ? C.accent : (v ? C.muted : C.borderDk) }}>{n(v)}</span> : null);
  const regionTotal = fc ? Object.values(fc.regions).reduce((a, b) => a + b, 0) : 0;
  const cityList = namedRegions.length === 1
    ? (REGION_CITIES[namedRegions[0]] || []).map(c => ({ c, v: fc?.cities[c] || 0 })).filter(x => x.v > 0 || cities.includes(x.c)).sort((a, b) => b.v - a.v)
    : [];

  // ═══ 렌더 ═══
  return (
    <div ref={rootRef} style={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0 }}>
      <GovTopBar left={topLeft}>
        <div style={{ width: 1, height: 22, background: C.border, margin: '0 4px', flexShrink: 0 }} />
        <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }}
          onChange={e => { const f = e.target.files?.[0]; if (f) void handleFile(f); }} />
        <Btn variant="secondary" onClick={() => fileRef.current?.click()} disabled={!!imp} style={smallBtn}>엑셀 올리기</Btn>
        <span title={syncTip} style={{ display: 'inline-flex' }}>
          <Btn variant="secondary" onClick={handleSync} disabled={syncDisabled} style={smallBtn}>지금 수집</Btn>
        </span>
        <span title="고친 방식을 배워 다음 분석에 먼저 적용합니다" style={{ display: 'inline-flex' }}>
          <Btn variant="secondary" onClick={() => setLearnOpen(true)} style={{ ...smallBtn, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            AI 학습{learnPct != null && <span style={{ fontSize: 10.5, fontWeight: 700, padding: '0 5px', borderRadius: 4, background: learnPct >= 85 ? C.greenLt : C.accentLt, color: learnPct >= 85 ? C.green : C.accent, fontVariantNumeric: 'tabular-nums' }}>{learnPct}%</span>}
          </Btn>
        </span>
        <div style={{ flex: 1, minWidth: 0, marginLeft: 6, display: 'flex', alignItems: 'center', gap: 10, overflow: 'hidden' }}>
          <div style={{ minWidth: 0, flexShrink: 1, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>{statusLine()}</div>
          {stats?.budget ? <BudgetMeter b={stats.budget} stats={stats} /> : stats && (
            <span title={`오늘\n${usageLines(stats.usage?.today)}\n\n누적\n${usageLines(stats.usage?.total)}`}
              style={{ fontSize: T.xs, color: C.muted, whiteSpace: 'nowrap', flexShrink: 0, fontVariantNumeric: 'tabular-nums', cursor: 'default' }}>
              오늘 AI 분석 <b style={{ color: C.sub, fontWeight: 600 }}>{stats.dailyUnlimited ? `${n(stats.analyzedToday)}건` : `${n(stats.analyzedToday)}/${n(stats.dailyMax)}`}</b>
              {stats.usage && <> · 약 <b style={{ color: C.sub, fontWeight: 600 }}>{fmtUsd(stats.usage.today.estUsd)}</b></>}
            </span>
          )}
        </div>
        {sources.length > 0 && rootW < 1560 && (() => {
          const latest = sources.map(x => x.lastAt || '').sort().pop() || '';
          const failed = sources.filter(x => x.ok === false).length;
          return (
            <span title={sources.map(x => `${x.label} ${x.ok === false ? `실패${x.error ? ` (${x.error})` : ''}` : x.lastAt ? fmtDateTime(x.lastAt) : '—'}`).join('\n')}
              style={{ fontSize: T.xs, color: C.muted, whiteSpace: 'nowrap', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
              <span style={{ fontWeight: 600, color: C.sub }}>수집</span> {latest ? fmtDateTime(latest) : '—'}
              {failed > 0 && <span style={{ color: C.red, fontWeight: 600 }}> · {failed}곳 실패</span>}
            </span>
          );
        })()}
        {sources.length > 0 && rootW >= 1560 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flexShrink: 1, overflow: 'hidden' }}>
            {sources.map(s => (
              <span key={s.key} title={[s.label, s.lastAt ? `마지막 수집 ${fmtDateTime(s.lastAt)}` : '수집 전', s.count != null ? `${n(s.count)}건` : '', s.error || ''].filter(Boolean).join(' · ')}
                style={{ fontSize: T.xs, whiteSpace: 'nowrap', color: s.ok === false ? C.red : C.muted, fontVariantNumeric: 'tabular-nums' }}>
                <span style={{ fontWeight: 600, color: s.ok === false ? C.red : C.sub }}>{s.label}</span> {s.running ? <span style={{ color: C.accent, fontWeight: 600 }}>수집 중</span> : s.key === 'bizinfo' && s.keyConfigured === false && !s.lastAt ? '키 없음' : s.ok === false ? '실패' : s.lastAt ? fmtDateTime(s.lastAt) : '—'}
              </span>
            ))}
          </div>
        )}
        {stats && !sources.length && <span style={{ fontSize: T.xs, color: C.muted, whiteSpace: 'nowrap', flexShrink: 0 }}>{stats.keyConfigured ? '자동 수집 전' : '자동 수집 꺼짐 (API 키 없음)'}</span>}
      </GovTopBar>
      {stats?.budget && (stats.budget.stopped || stats.pipelineState === 'budget_stopped') && (
        <BudgetBanner b={stats.budget} pending={stats.counts.buckets?.pending ?? stats.counts.aiQueued} onRaise={() => setBudgetOpen(true)} />
      )}

      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* ═══ COL 1: 필터 ═══ */}
        {!hideFilters && (
          <div style={{ width: 184, flexShrink: 0, borderRight: `1px solid ${C.border}`, overflowY: 'auto', padding: '14px 12px' }}>
            <SearchInput value={search} onChange={setSearch} placeholder="공고명·기관" style={{ marginBottom: 14 }} />

            {/* 지역 — 여러 곳 · 광역 하나면 시·군까지 (2026-10-03) */}
            <div style={{ ...secTitle, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>지역</span>
              {namedRegions.length > 0 && !regions.includes('전국') && (
                <label title="지역을 정하지 않은 전국 공고도 함께 봅니다" style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10.5, fontWeight: 500, color: nat ? C.accent : C.muted, cursor: 'pointer' }}>
                  <input type="checkbox" checked={nat} onChange={e => setNat(e.target.checked)} style={{ margin: 0, width: 11, height: 11, accentColor: C.accent }} />전국 포함
                </label>
              )}
            </div>
            <div style={{ marginBottom: cityList.length ? 8 : 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <div onClick={() => { setRegions([]); setCities([]); }} style={{ ...filterItem(!regions.length), gridColumn: '1 / -1', padding: '5px 8px' }}>
                <span>전체</span>{cnt(regionTotal || undefined, !regions.length)}
              </div>
              {['전국', ...REGION_GRID].map(r => {
                const on = regions.includes(r);
                const v = fc?.regions[r];
                return (
                  <div key={r} onClick={() => setRegions(prev => toggleIn(prev, r))} title={r === '전국' ? '지역을 정하지 않은 공고' : `${r} 공고${v != null ? ` ${n(v)}건` : ''} — 여러 곳 고를 수 있습니다`}
                    style={{ ...filterItem(on), padding: '5px 6px', gap: 2, opacity: fc && !v && !on ? 0.55 : 1 }}>
                    <span>{on ? '✓' : ''}{r}</span>{cnt(v, on)}
                  </div>
                );
              })}
            </div>
            {cityList.length > 0 && (
              <div style={{ marginBottom: 16, padding: '8px 8px 6px', borderRadius: R.sm, background: C.bgAlt, border: `1px solid ${C.border}` }}>
                <div style={{ fontSize: 10.5, color: C.muted, marginBottom: 6, lineHeight: 1.4 }}>{namedRegions[0]} 시·군 — 고르면 그곳 + {namedRegions[0]} 전체 공고</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                  {cityList.map(({ c, v }) => {
                    const on = cities.includes(c);
                    return (
                      <button key={c} type="button" onClick={() => setCities(prev => toggleIn(prev, c))}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 6px', borderRadius: 10, cursor: 'pointer', fontSize: 11,
                          border: `1px solid ${on ? C.accent : C.border}`, background: on ? C.accentLt : C.white, color: on ? C.accent : C.text, fontWeight: on ? 600 : 400 }}>
                        {c.replace(/(시|군|구)$/, '')}<span style={{ ...countTag, fontSize: 10, color: on ? C.accent : C.muted }}>{v}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 분류 — 우리 분류 체계 (대분류를 고르면 세부까지) */}
            <div style={secTitle}>분류</div>
            <div style={{ marginBottom: 16 }}>
              <div onClick={() => setCats([])} style={filterItem(!cats.length)}><span>전체</span></div>
              {GOV_TAXONOMY.map(g => {
                const gOn = cats.includes(g.name);
                const open = catOpen === g.k || g.items.some(i => cats.includes(i));
                return (
                  <div key={g.k}>
                    <div style={{ ...filterItem(gOn), padding: '6px 6px 6px 2px' }} onClick={() => setCats(prev => toggleIn(prev.filter(x => !g.items.includes(x)), g.name))}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                        <span role="button" aria-label={open ? '세부 접기' : '세부 펼치기'} onClick={e => { e.stopPropagation(); setCatOpen(open && catOpen === g.k ? null : g.k); }}
                          style={{ width: 14, textAlign: 'center', fontSize: 9, color: C.muted, flexShrink: 0, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .12s' }}>▶</span>
                        {gOn ? '✓ ' : ''}{g.name}
                      </span>
                      {cnt(fc?.catGroups[g.name], gOn)}
                    </div>
                    {open && g.items.map(it => {
                      const on = cats.includes(it);
                      const inc = gOn && !on;
                      return (
                        <div key={it} onClick={() => setCats(prev => toggleIn(prev.filter(x => x !== g.name), it))}
                          style={{ ...filterItem(on), padding: '4px 6px 4px 22px', fontSize: 11.5, color: on ? C.accent : inc ? C.sub : C.text }}>
                          <span>{on ? '✓ ' : ''}{it}</span>{cnt(fc?.cats[it], on)}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
              {/* 전용 대상 = 기업 유형 (2026-10-05g — 업종 전용 분야는 하위 업종으로 옮겼다 · 옛 분석의 전용 분야 라벨은 건수가 있을 때만) */}
              {([['전용 대상', [...GOV_TYPES, ...GOV_ARTS.filter(a => (fc?.cats[a] || 0) > 0 || cats.includes(a))]]] as [string, string[]][]).map(([title, list]) => (
              <Fragment key={title}>
              <div style={{ fontSize: 10.5, color: C.muted, margin: '8px 0 4px 2px' }}>{title}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                {list.map(a => {
                  const on = cats.includes(a);
                  const [bd, bg, fg] = GOV_TYPES.includes(a) ? ['#7FB8A8', '#E6F4EF', '#0F5A47'] : ['#D4A017', '#FBF3DD', '#7A5A00'];
                  return (
                    <button key={a} type="button" onClick={() => setCats(prev => toggleIn(prev, a))}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 8px', borderRadius: 10, cursor: 'pointer', fontSize: 11,
                        border: `1px solid ${on ? bd : C.border}`, background: on ? bg : C.white, color: on ? fg : C.text, fontWeight: on ? 600 : 400 }}>
                      {a}{fc && <span style={{ ...countTag, fontSize: 10, color: on ? fg : C.muted }}>{n(fc.cats[a])}</span>}
                    </button>
                  );
                })}
              </div>
              </Fragment>
              ))}
            </div>

            <div style={secTitle}>출처</div>
            <div style={{ marginBottom: 16 }}>
              {['', ...(stats?.sources?.length ? stats.sources.map(x => x.key) : SOURCES)].map(s => {
                const st = stats?.sources?.find(x => x.key === s);
                return (
                  <div key={s || 'all'} onClick={() => setSource(s)} style={filterItem(source === s)}>
                    <span>{s ? sourceLabel(s) : '전체'}</span>{st?.count != null && <span style={countStyle(source === s)}>{n(st.count)}</span>}
                  </div>
                );
              })}
            </div>

            <div style={secTitle}>더 좁히기</div>
            <div style={{ marginBottom: 12 }}>
              {([['recur', '반복 사업만', recur, setRecur, undefined], ['dup', '이미 등록된 것', dupOnly, setDupOnly, counts?.dup],
                ['aierr', 'AI 오류', aiErr, setAiErr, counts?.aiError]] as [string, string, boolean, (v: boolean) => void, number | undefined][])
                .map(([k, label, on, set, c]) => (
                  <div key={k} onClick={() => set(!on)} style={filterItem(on)}>
                    <span>{on ? '✓ ' : ''}{label}</span>{c != null && c > 0 && <span style={countStyle(on)}>{n(c)}</span>}
                  </div>
                ))}
            </div>
            {counts && counts.aiError > 0 && stats?.aiConfigured && (
              <button onClick={() => handlePipeline(true)} style={{ ...linkBtn, width: '100%', justifyContent: 'center', marginBottom: 8 }}>AI 오류 {n(counts.aiError)}건 다시 분석</button>
            )}
            {filterActive && <button onClick={clearFilters} style={{ ...textBtn, color: C.sub, width: '100%' }}>필터 모두 해제</button>}
          </div>
        )}

        {/* ═══ COL 2+3: 탭 · 표 · 패널 ═══ */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0, background: C.white }}>
          {/* 탭 */}
          <div role="tablist" style={{ display: 'flex', alignItems: 'flex-end', gap: 2, padding: '0 16px', borderBottom: `1px solid ${C.border}`, flexShrink: 0, overflowX: 'auto' }}>
            {[...BUCKETS, { k: 'done' as Tab, label: '처리 완료', dot: '', tip: undefined as string | undefined }].map(b => {
              const act = tab === b.k;
              const c = tabCount(b.k);
              return (
                <button key={b.k} role="tab" aria-selected={act} title={b.tip} onClick={() => { if (tab !== b.k) { openRow(null); setTab(b.k); } }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '11px 12px 9px', border: 'none', background: 'transparent', cursor: 'pointer',
                    borderBottom: `2px solid ${act ? C.accent : 'transparent'}`, marginBottom: -1, whiteSpace: 'nowrap',
                    fontSize: T.sm, fontWeight: act ? 700 : 500, color: act ? C.text : C.sub,
                  }}>
                  {b.dot && <span style={{ width: 7, height: 7, borderRadius: 4, background: b.dot, flexShrink: 0 }} />}
                  {b.label}
                  {c != null && <span style={{ fontSize: T.xs, fontWeight: 700, color: act ? C.accent : C.muted, fontVariantNumeric: 'tabular-nums' }}>{n(c)}</span>}
                </button>
              );
            })}
                {stats?.scope && (() => {
                  const sc = stats.scope;
                  const mode: GovFeedScopeMode = sc.mode || (sc.local ? 'local' : 'capital');
                  const seg = (on: boolean, label: React.ReactNode, m: GovFeedScopeMode, tip: string) => (
                    <button type="button" disabled={scopeBusy} title={tip}
                      onClick={() => { if (on) return; if (m !== 'manual' && scopeArm !== m) { setScopeArm(m); return; } void changeScope(m); }}
                      style={{ height: 22, padding: '0 8px', border: 'none', borderRadius: 5, cursor: on ? 'default' : 'pointer', fontSize: 11, whiteSpace: 'nowrap',
                        background: scopeArm === m ? C.red : on ? C.white : 'transparent', color: scopeArm === m ? C.white : on ? C.text : C.muted, fontWeight: on || scopeArm === m ? 700 : 500, boxShadow: on ? '0 1px 2px rgba(17,24,39,0.10)' : 'none' }}>
                      {scopeArm === m ? `한 번 더 누르면 분석 시작(${n(m === 'pick' ? (sc.waitingStarred ?? 0) : m === 'local' ? sc.waitingLocal : sc.waitingCapital)}건 · 비용)` : label}</button>
                  );
                  return (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0, marginLeft: 'auto', paddingLeft: 12, alignSelf: 'center' }}>
                      <span title={mode === 'manual' ? '★는 표시만 합니다 — AI 심층 분석을 하지 않아 비용이 들지 않습니다. 판독은 크롬에서 판독 가이드대로 직접 합니다.' : mode === 'pick' ? '공고는 AI 가 간략히 선별만 하고, 심층 분석은 ★로 고른 것만 합니다' : '매일 새로 들어오는 공고는 지역과 상관없이 모두 분석합니다(비추천만 제외). 이전에 쌓인 공고만 이 범위를 따릅니다.'} style={{ fontSize: T.xs, color: C.muted, whiteSpace: 'nowrap', cursor: 'help' }}>{mode === 'pick' || mode === 'manual' ? '심층 분석' : '지난 공고 분석'}</span>
                      <span style={{ display: 'inline-flex', padding: 2, gap: 2, borderRadius: 7, background: C.bg }}>
                        {/* 수기 판독(2026-10-17 지도사 「★ 쳤을 때 자동 분석 꺼 줘 — 하루 2~3만 원 · 크롬에서 수기로 판독」) */}
                        {seg(mode === 'manual', '수기 판독', 'manual', '★를 쳐도 AI 심층 분석을 하지 않습니다(비용 없음) — 크롬에서 판독 가이드대로 직접 등록합니다. 새 공고의 간략 선별은 그대로')}
                        {seg(mode === 'pick', <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><span style={{ color: '#F59E0B', display: 'inline-flex' }}><StarIcon on size={10} /></span>체크한 것만{sc.waitingStarred ? ` ${n(sc.waitingStarred)}` : ''}</span>, 'pick',
                          `★(분석 대상)로 고른 공고만 심층 분석합니다 — 새로 들어온 공고도 AI 가 간략히 선별해 「분석 대기」에 넣어 두고, ☆를 누르면 그때 분석합니다. 고른 것 ${n(sc.waitingStarred ?? 0)}건 대기`)}
                        {seg(mode === 'capital', '수도권·전국', 'capital', `이전에 쌓인 공고는 서울·경기·인천·전국을 모두 분석합니다 (새로 들어오는 공고는 지역 상관없이 모두) — 분석 대기 ${n(sc.waitingCapital)}건`)}
                        {seg(mode === 'local', `+지방 ${n(sc.waitingLocal)}`, 'local', `이전에 쌓인 지방 공고까지 분석합니다 — 수도권·전국을 먼저, 그다음 지방 (${n(sc.waitingLocal)}건 추가)`)}
                      </span>
                    </span>
                  );
                })()}
          </div>

          {/* 선택 · 일괄 처리 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', minHeight: 48, boxSizing: 'border-box', borderBottom: `1px solid ${C.border}`, flexShrink: 0, background: selCount ? '#F8FAFF' : C.white }}>
            {tab === 'done' && (
              <div style={{ display: 'inline-flex', padding: 2, gap: 2, borderRadius: 8, background: C.bg, flexShrink: 0 }}>
                {([['approved', '승인됨', counts?.approved], ['excluded', '제외됨', counts?.excluded]] as ['approved' | 'excluded', string, number | undefined][]).map(([k, label, c]) => (
                  <button key={k} onClick={() => { openRow(null); setDoneReview(k); }} style={{
                    padding: '4px 10px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: T.xs, whiteSpace: 'nowrap',
                    background: doneReview === k ? C.white : 'transparent', color: doneReview === k ? C.text : C.sub,
                    fontWeight: doneReview === k ? 700 : 500, boxShadow: doneReview === k ? '0 1px 2px rgba(17,24,39,0.10)' : 'none',
                  }}>{label}{c != null ? ` ${n(c)}` : ''}</button>
                ))}
              </div>
            )}
            {/* 관심(별표) — 전체 ↔ 관심만 (2026-10-03) */}
            <div role="group" aria-label="관심 공고만 보기" style={{ display: 'inline-flex', padding: 2, gap: 2, borderRadius: 8, background: C.bg, flexShrink: 0 }}>
              {([[false, tab === 'pending' ? '고를 것' : '전체'], [true, '관심']] as [boolean, string][]).map(([v, label]) => {
                const on = starOnly === v;
                return (
                  <button key={label} onClick={() => { if (starOnly !== v) { openRow(null); setStarOnly(v); } }} aria-pressed={on}
                    title={v ? '★ 표시한 공고만 — 이 탭·지역·분류 안에서 (S 키로 담기·빼기)' : '모든 공고'}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: T.xs, whiteSpace: 'nowrap',
                      background: on ? (v ? '#FFFBEB' : C.white) : 'transparent', color: on ? (v ? '#B45309' : C.text) : C.sub,
                      fontWeight: on ? 700 : 500, boxShadow: on ? '0 1px 2px rgba(17,24,39,0.10)' : 'none' }}>
                    {v && <span style={{ color: on || (facets?.starred ?? 0) > 0 ? '#F59E0B' : C.borderDk, display: 'inline-flex' }}><StarIcon on size={12} /></span>}
                    {label}
                    {v && facets && <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: on ? '#B45309' : C.muted }}>{n(facets.starred ?? 0)}</span>}
                  </button>
                );
              })}
            </div>
            <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 4, fontSize: T.sm, color: C.sub, whiteSpace: 'nowrap', overflow: 'hidden' }}>
              {selCount > 0 ? (
                <>
                  <span style={{ fontWeight: 700, color: C.accent, fontVariantNumeric: 'tabular-nums' }}>{n(selCount)}건 선택</span>
                  {allShownChecked && shownTotal > selCount && (
                    <button onClick={selectAllInTab} disabled={selectingAll} style={textBtn}>{selectingAll ? '불러오는 중…' : `이 탭 전체 ${n(shownTotal)}건 선택`}</button>
                  )}
                  <button onClick={() => setSel(new Map())} style={{ ...textBtn, color: C.muted }}>해제</button>
                </>
              ) : (
                <span style={{ color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  <b className="gf-num" style={{ color: C.sub, fontVariantNumeric: 'tabular-nums' }}>{listLoading && !rows.length ? '…' : n(shownTotal)}건</b>
                  {scopeText && <> · <span title="왼쪽 지역·분류로 좁힌 조건" style={{ color: C.accent, fontWeight: 600 }}>{scopeText}</span> <button onClick={() => { setRegions([]); setCities([]); setCats([]); }} title="지역·분류 해제" style={{ ...textBtn, color: C.muted, padding: '0 2px' }}>✕</button></>}
                  {!panelOpen && rootW > 1000 && <> · 칸을 눌러 바로 고치고, 행을 누르면 검토 패널 · ↑↓ 이동 Space 선택</>}
                </span>
              )}
            </div>
            {/* 사업명 검색 — 표 위 오른쪽(왼쪽 칸을 내려도 보이게 · 왼쪽 「공고명·기관」 칸과 같은 값 · 2026-10-14 지도사 「사업명으로 검색하는 기능 작게 구석이라도」) */}
            <span style={{ position: 'relative', flexShrink: 0, display: 'inline-flex' }}>
              <SearchInput value={search} onChange={setSearch} placeholder="사업명 검색" style={{ width: 170, height: 30, padding: '0 26px 0 10px', fontSize: T.xs }} />
              {search && <button type="button" aria-label="검색어 지우기" onClick={() => setSearch('')}
                style={{ position: 'absolute', right: 4, top: 5, width: 20, height: 20, border: 'none', background: 'none', color: C.muted, cursor: 'pointer', fontSize: 13, lineHeight: '20px', padding: 0 }}>✕</button>}
            </span>
            {/* 교통정리 (2026-10-03): 분석 대기 탭의 ★ = 심층 분석 요청(자동) · 분석된 탭의 ★ = 관심 → 다시 분석은 이 단추로만 */}
            {starOnly && selCount === 0 && tab === 'pending' && (
              <span title={stats?.scope?.mode === 'manual'
                ? '수기 판독 — ★ 는 표시만 합니다(AI 로 보내지 않아 비용 없음). 「일괄」 표시 공고는 수기 판독으로 바꾸기 전에 이미 보낸 것 — 결과만 받습니다'
                : '분석 대기에서 ★ 를 누르면 그 공고만 AI 가 심층 분석합니다 — ★ 를 다시 누르면 요청 취소(아직 보내기 전이면 비용 없음)'}
                style={{ fontSize: T.xs, color: C.sub, display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                <span style={{ color: '#F59E0B', display: 'inline-flex' }}><StarIcon on size={11} /></span>
                {stats?.scope?.mode === 'manual' ? '= 표시만 · 수기 판독(비용 없음)' : '= 심층 분석 요청 · 자동 진행'}{/* 2026-10-17 지도사 「AI 일괄 분석 안 한다며 한다잖아」 — 수기 판독인데 옛 문구가 남아 있었다 */}
              </span>
            )}
            {starOnly && selCount === 0 && tab !== 'done' && tab !== 'pending' && (
              <Btn variant="secondary" onClick={reanalyzeStarred} disabled={reBusy || !(facets?.starred)}
                style={{ ...smallBtn, display: 'inline-flex', alignItems: 'center', gap: 5, color: C.accent }}>
                <span title="이미 분석된 관심 공고를, 고치신 값을 학습한 AI 로 다시 분석합니다 (분석 대기의 ★ 는 자동 분석이라 여기 안 들어감)">{reBusy ? '확인 중…' : '분석된 관심 다시 분석 (AI 학습 반영)'}</span>
              </Btn>
            )}
            {selCount > 0 && (() => {
              const allStar = selRows.every(r => r.starred);
              return (
                <Btn variant="secondary" onClick={() => toggleStar(selRows.map(r => r.id), !allStar)} disabled={bulkBusy}
                  style={{ ...smallBtn, display: 'inline-flex', alignItems: 'center', gap: 5, color: allStar ? C.sub : '#B45309' }}>
                  <span style={{ color: allStar ? C.borderDk : '#F59E0B', display: 'inline-flex' }}><StarIcon on={!allStar} size={12} /></span>
                  {allStar ? '관심 해제' : '관심 담기'}
                </Btn>
              );
            })()}
            {tab === 'done' ? (
              doneReview === 'excluded' && <Btn variant="secondary" onClick={() => handleBulk('restore')} disabled={!selCount || bulkBusy} style={{ ...smallBtn, color: C.green }}>선택 복원</Btn>
            ) : (
              <>
                <Btn variant="secondary" onClick={() => handleBulk('exclude')} disabled={!selCount || bulkBusy} style={{ ...smallBtn, color: selCount ? C.red : C.sub }}>선택 제외</Btn>
                <Btn onClick={() => setBulkRows(selRows)} disabled={!canApprove || bulkBusy} style={{ ...smallBtn, padding: '6px 14px' }}>
                  선택 일괄 등록{selCount ? ` (${n(selCount)}건)` : ''}
                </Btn>
              </>
            )}
          </div>

          {/* 표 | 패널 — 집중 모드면 이 칸이 통째로 화면 가득 덮개(고정)가 된다. 자식 순서는 그대로라 원문·양식이 다시 그려지지 않는다 */}
          <div ref={focusBoxRef} role={focusActive ? 'dialog' : undefined} aria-modal={focusActive || undefined} aria-label={focusActive ? '집중 모드' : undefined}
            style={focusActive ? {
              position: 'fixed', inset: 0, zIndex: 1050, background: C.white, display: 'grid',
              gridTemplateAreas: focusNarrow ? '"strip" "viewer" "split" "form"' : '"strip strip strip" "viewer split form"',
              gridTemplateColumns: focusNarrow ? 'minmax(0, 1fr)' : `minmax(0, ${splitH}fr) 7px minmax(0, ${100 - splitH}fr)`,
              gridTemplateRows: focusNarrow
                ? (viewerOnlyEff ? `${STRIP_H}px minmax(0, 1fr) 0 0` : `${STRIP_H}px minmax(0, ${splitV}fr) 7px minmax(0, ${100 - splitV}fr)`)
                : `${STRIP_H}px minmax(0, 1fr)`,
              userSelect: fDrag ? 'none' : undefined, animation: 'gfFocusIn 0.12s ease-out',
            } : { flex: 1, minHeight: 0, display: 'flex' }}>
            {focusActive ? (
              <div style={{ gridArea: 'strip', display: 'flex', alignItems: 'center', gap: 8, padding: '0 12px 0 14px', borderBottom: `1px solid ${C.border}`, background: C.white, minWidth: 0 }}>
                <style>{FOCUS_CSS}</style>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, height: 22, padding: '0 8px', borderRadius: R.sm, background: C.accentLt, color: C.accent, fontSize: T.xs, fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0 }}>
                  <FocusIcon />집중 모드
                </span>
                <button className="gf-sbtn" onClick={e => { e.currentTarget.blur(); move(-1); }} disabled={idx <= 0} style={{ ...stripIcon, opacity: idx <= 0 ? 0.4 : 1 }} aria-label="이전 공고" title="이전 (↑)">↑</button>
                <button className="gf-sbtn" onClick={e => { e.currentTarget.blur(); move(1); }} disabled={idx >= Math.max(rows.length, shownTotal, 1) - 1} style={{ ...stripIcon, opacity: idx >= Math.max(rows.length, shownTotal, 1) - 1 ? 0.4 : 1 }} aria-label="다음 공고" title="다음 (↓)">↓</button>
                <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 6, fontSize: T.sm, whiteSpace: 'nowrap', overflow: 'hidden' }}>
                  <span style={{ flexShrink: 0, color: C.sub, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{n(Math.max(0, idx) + 1)} / {n(Math.max(rows.length, shownTotal, 1))}</span>
                  <span style={{ flexShrink: 0, color: C.borderDk }}>·</span>
                  <span title={focusTitle} style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', color: C.text, fontWeight: 600 }}>{focusTitle || '불러오는 중…'}</span>
                </div>
                {focusNarrow && (
                  <button className="gf-sbtn" onClick={e => { e.currentTarget.blur(); setViewerOnly(v => !v); }} aria-pressed={viewerOnly}
                    style={{ ...stripBtn, ...(viewerOnly ? { borderColor: C.accent, color: C.accent, background: C.accentLt, fontWeight: 600 } : {}) }}>
                    {viewerOnly ? '양식 같이 보기' : '원문만 크게'}
                  </button>
                )}
                <button className="gf-sbtn" onClick={e => { e.currentTarget.blur(); setFocusOn(false); }} title="목록으로 돌아가기 (Esc · F)" style={{ ...stripBtn, color: C.text, fontWeight: 600 }}>
                  집중 모드 끄기<span style={stripKbd}>Esc</span>
                </button>
              </div>
            ) : null}
            <div ref={tableBoxRef} style={{ width: panelOpen ? listW : undefined, flex: panelOpen ? '0 0 auto' : 1, minWidth: 0, display: focusActive ? 'none' : 'flex', flexDirection: 'column', borderRight: panelOpen ? `1px solid ${C.border}` : 'none', transition: dragging ? 'none' : 'width 0.15s' }}>
              {!listLoading && rows.length === 0 ? (
                <Empty icon={starOnly ? '☆' : '📭'} message={starOnly ? (filterActive ? '이 조건의 관심 공고가 없습니다' : '이 탭에 관심 표시한 공고가 없습니다 — 목록의 ☆를 누르거나 S 키로 담아 두세요') : filterActive ? '조건에 맞는 공고가 없습니다' : tab === 'ready' ? '바로 등록할 공고가 없습니다 — 「확인 필요」 탭을 보세요' : tab === 'done' ? '처리한 공고가 없습니다' : tab === 'past' ? PAST_NOTE : '이 탭에 공고가 없습니다'}
                  action={starOnly ? <button onClick={() => setStarOnly(false)} style={linkBtn}>전체 보기</button> : filterActive ? <button onClick={clearFilters} style={linkBtn}>필터 해제</button> : undefined} />
              ) : (
                <GovFeedTable
                  rows={rows} width={tableW} compact={panelOpen} slim={panelOpen && listSlim} sort={sort} onSort={onSort}
                  onToggleList={panelOpen && listCanWiden ? () => setListPref(listSlim ? 'wide' : 'slim') : undefined} selId={selId} cursorId={cursorId} checked={sel}
                  rowErrors={rowErrors} savingIds={savingIds} showReview={tab === 'done'} scrollRef={scrollRef}
                  onCheck={toggleCheck} onCheckAll={toggleAllShown}
                  onOpen={id => { if (id === selId) openRow(null); else openRow(id); }}
                  onEdit={(row, f, anchor) => setEdit({ row, field: f, anchor })}
                  onStar={(id, on) => { if (triage && on) pickRow(id); else void toggleStar([id], on); }}
                  onDrop={triage ? (id => { void dropRow(id); }) : undefined}
                  footer={(
                    <div ref={sentinelRef} style={{ padding: '14px 0', textAlign: 'center', fontSize: T.xs, color: C.muted }}>
                      {listLoading ? '불러오는 중…' : hasMore ? <button onClick={() => load(page + 1, true)} style={linkBtn}>더 보기 ({n(rows.length)} / {n(total)})</button> : rows.length > 20 ? '끝' : ''}
                    </div>
                  )}
                />
              )}
            </div>
            {panelOpen && selId && (viewerOpen || focusActive ? (
              <>
                <div style={focusActive ? { gridArea: 'viewer', display: 'flex', minWidth: 0, minHeight: 0 } : { width: viewerWEff, flexShrink: 0, display: 'flex', minWidth: 0, minHeight: 0 }}>
                  <GovFeedDocViewer groupId={selId} focus={focus} onPick={onPick} onCollapse={focusActive ? undefined : () => setViewerOpen(false)} fitKey={focusActive} dragging={dragging || fDrag} />
                </div>
                {focusActive ? (
                  <div onPointerDown={startFocusDrag} onDoubleClick={() => (focusNarrow ? setSplitV(50) : setSplitH(55))} onKeyDown={onSplitKey} tabIndex={0}
                    role="separator" aria-orientation={focusNarrow ? 'horizontal' : 'vertical'} aria-label="원문·양식 나눔" aria-valuemin={SPLIT_MIN} aria-valuemax={SPLIT_MAX} aria-valuenow={Math.round(focusNarrow ? splitV : splitH)}
                    title="끌어서 크기 조절 · 두 번 누르면 기본"
                    className={`gf-fsplit ${focusNarrow ? 'v' : 'h'}${fDrag ? ' on' : ''}`} style={{ gridArea: 'split', display: viewerOnlyEff ? 'none' : undefined, minWidth: 0, minHeight: 0 }} />
                ) : (
                  <div onMouseDown={startDrag} onDoubleClick={() => setViewerW(500)} title="끌어서 폭 조절 · 두 번 누르면 기본 폭" role="separator" aria-orientation="vertical"
                    className="gf-split" style={{ width: 6, flexShrink: 0, cursor: 'col-resize', background: dragging ? C.accentLt : C.bgAlt, borderLeft: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`, position: 'relative' }}>
                    <span style={{ position: 'absolute', top: '50%', left: 1, width: 2, height: 28, marginTop: -14, borderRadius: 1, background: dragging ? C.accent : C.borderDk }} />
                  </div>
                )}
              </>
            ) : (
              <button onClick={() => setViewerOpen(true)} title="원문 펼치기" aria-label="원문 펼치기"
                style={{ width: 34, flexShrink: 0, border: 'none', borderRight: `1px solid ${C.border}`, background: C.bgAlt, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, paddingTop: 12, color: C.sub }}>
                <span style={{ fontSize: T.xs }}>»</span>
                <span style={{ writingMode: 'vertical-rl', fontSize: T.sm, fontWeight: 600, letterSpacing: 2 }}>원문 보기</span>
              </button>
            ))}
            {panelOpen && (
              <div style={focusActive
                ? { gridArea: 'form', display: viewerOnlyEff ? 'none' : 'flex', minWidth: 0, minHeight: 0 }
                : { flex: 1, display: 'flex', minWidth: 0, minHeight: 0 }}>
                <GovFeedPanel
                  detail={detail && detail.group.id === selId ? detail : null} loading={detailLoading}
                  form={form} setForm={setForm} applyMode={applyMode} setApplyMode={setApplyMode}
                  saveState={saveState} busy={busy} aiConfigured={stats?.aiConfigured !== false}
                  index={Math.max(0, idx)} total={Math.max(rows.length, shownTotal, 1)}
                  onPrev={() => move(-1)} onNext={() => move(1)} onClose={() => openRow(null)}
                  onApprove={handleApprove} onSaveRegistered={handleSaveRegistered} regName={detail ? (regOf(detail)?.name ?? null) : null} onExclude={handleExclude} onRestore={handleRestore} onAnalyze={handleAnalyze}
                  onQuote={onQuote} focusMode={focusActive} onFocusMode={() => setFocusOn(true)}
                  onStar={on => { if (selId) void toggleStar([selId], on); }}
                  onSplit={async childId => {
                    if (!selId) return;
                    try {
                      await api.splitGovFeed(selId, childId);
                      setDetail(d => (d && d.group.id === selId ? { ...d, sameNotices: (d.sameNotices || []).filter(x => x.id !== childId) } : d));
                      patchRow(selId, r => ({ ...r, dupCount: Math.max(0, (r.dupCount || 0) - 1) }));
                      setToast('따로 목록에 올렸어요 — 새로고침하면 보입니다');
                    } catch (e) { setToast(errMsg(e, '따로 보기에 실패했습니다')); }
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {edit && (
        <FeedCellEditor row={rows.find(r => r.id === edit.row.id) || edit.row} field={edit.field} anchor={edit.anchor}
          onClose={() => setEdit(null)} onSave={patch => saveCell(edit.row, patch)} />
      )}

      {learnOpen && (
        <GovFeedLearnDrawer stats={stats} onClose={() => setLearnOpen(false)} onToast={setToast} onInfo={setLearnInfo} onEditBudget={() => setBudgetOpen(true)}
          onRequeued={() => { void refreshStats(); void load(1, false); }} />
      )}

      {budgetOpen && stats?.budget && (
        <BudgetDialog b={stats.budget} onClose={() => setBudgetOpen(false)}
          onSaved={nb => { setBudgetOpen(false); setStats(s => (s ? { ...s, budget: nb, pipelineState: nb.stopped ? s.pipelineState : 'running' } : s)); setToast(`예산 ${Math.round(nb.krw / 10000).toLocaleString()}만 원으로 저장${nb.resumed ? ' — 분석을 다시 시작합니다' : ''}`); void refreshStats(); }} />
      )}

      {bulkRows && <GovFeedBulkDialog rows={bulkRows} onClose={() => setBulkRows(null)} run={runBulkApprove} />}

      {toast && <div style={{ position: 'fixed', bottom: 76, left: '50%', transform: 'translateX(-50%)', padding: '10px 20px', borderRadius: R.md, background: C.text, color: C.white, fontSize: T.sm, fontWeight: 600, zIndex: 9999, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center', gap: 12 }}>{toast}
        {undoAct && undoAct.msg === toast && <button onClick={() => { const a = undoAct; setUndoAct(null); setToast(''); a.run(); }} style={{ border: 'none', background: 'transparent', color: '#93C5FD', fontWeight: 700, fontSize: T.sm, cursor: 'pointer', padding: 0 }}>되돌리기</button>}</div>}
    </div>
  );
}
